import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Stack,
  IconButton,
  Tooltip,
  Collapse,
  Pagination,
  Tabs,
  Tab,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Autocomplete,
  ToggleButton,
  ToggleButtonGroup,
  Checkbox,
  FormControlLabel,
  LinearProgress,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import FilterListIcon from "@mui/icons-material/FilterList";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import VerifiedIcon from "@mui/icons-material/Verified";
import RefreshIcon from "@mui/icons-material/Refresh";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { toast } from "react-toastify";
import api from "../../api";
import LoadError from "../../components/admin/LoadError";
import RowActions from "../../components/admin/RowActions";
import StatusChip from "../../components/admin/StatusChip";
import { formatDate, formatDateTime, humanize, clampSx, EMPTY } from "../../utils/adminFormat";
import HtmlWithLatex from "../QuestionBank/components/HtmlWithLatex";
import QuestionBankQuestionEditDialog from "../QuestionBank/components/QuestionBankQuestionEditDialog";
import QuestionVerifyDialog from "../../components/QuestionVerifyDialog";
import VideoVerificationPanel from "../ExplanationVideos/VideoVerificationPanel";

const STUDENT_BASE = process.env.REACT_APP_STUDENT_PREFIX || "student";

function flattenExamCategories(nodes, depth = 0) {
  return (Array.isArray(nodes) ? nodes : []).flatMap((n) => [
    { id: n.id, name: `${"— ".repeat(depth)}${n.name}` },
    ...flattenExamCategories(n.all_children ?? n.allChildren ?? n.children ?? [], depth + 1),
  ]);
}

function getCurrentUserId() {
  try {
    return JSON.parse(localStorage.getItem("userData") || "{}")?.id ?? null;
  } catch {
    return null;
  }
}

const STATUS_CONFIG = {
  approved: { label: "Approved", color: "success", icon: <CheckCircleOutlineIcon fontSize="small" /> },
  rejected: { label: "Rejected", color: "error",   icon: <CancelOutlinedIcon fontSize="small" /> },
  flagged:  { label: "Flagged",  color: "warning",  icon: <CancelOutlinedIcon fontSize="small" /> },
};

const FIELD_LABELS = {
  question_text: "Question Text",
  explanation: "Explanation",
  default_marks: "Default Marks",
  difficulty: "Difficulty",
  options: "Options",
  sub_questions: "Sub-questions",
  image_url: "Image",
  image_urls: "Images",
  correct_answer: "Correct Answer",
  correct_answer_text: "Answer Text",
  correct_blanks: "Blank Answers",
  section_instructions: "Section Instructions",
  type: "Question Type",
};

const fieldLabel = (key) => FIELD_LABELS[key] ?? key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const stripHtml = (s) => s?.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").replace(/&amp;/g, "&").trim() ?? "";

const PLATFORM_COLORS = { web: "info", mobile: "secondary" };

// ── Layout pieces shared by every tab (admin table standard, 31 Oct) ─────────

const BRAND = "#3B2A9F";

const TAB_GROUPS = [
  {
    label: "Content",
    tabs: [
      { value: "items", label: "By Item", hint: "Every verified question with its verdicts and web / mobile coverage." },
      { value: "papers", label: "By Paper", hint: "How much of each past or practice paper has been verified, question by question." },
      { value: "worksheets", label: "By Worksheet", hint: "How much of each worksheet has been verified, question by question." },
      { value: "mock-exams", label: "By Mock Exam", hint: "How much of each mock exam has been verified, question by question." },
      { value: "videos", label: "Videos", hint: "Explanation video sets and how many videos a tester has approved." },
    ],
  },
  {
    label: "Testers",
    tabs: [
      { value: "records", label: "Individual Records", hint: "Every single verdict a tester has given, newest first." },
      { value: "edits", label: "Tester Edits", hint: "Before → after of every question, answer and explanation change a tester made." },
      { value: "activity", label: "Tester Activity", hint: "Verdicts grouped by tester for the chosen dates." },
      { value: "tester-stats", label: "Tester Stats", hint: "Totals per tester: approved, rejected, flagged and unique items." },
    ],
  },
];
const ALL_TABS = TAB_GROUPS.flatMap((g) => g.tabs);
const TAB_VALUES = new Set(ALL_TABS.map((t) => t.value));

const VERIFIABLE_TYPE_LABELS = {
  question: "Question",
  worksheet: "Worksheet",
  paper: "Paper",
  mock_exam: "Mock exam",
  explanation_video: "Video",
};
const verifiableTypeLabel = (t) => VERIFIABLE_TYPE_LABELS[t] ?? humanize(t);

function TypeChip({ type }) {
  return <Chip label={verifiableTypeLabel(type)} size="small" variant="outlined" sx={{ height: 22, fontSize: "0.72rem" }} />;
}

const fmtNum = (n) => Number(n ?? 0).toLocaleString("en-GB");

const TABLE_SX = { borderRadius: 2, border: "1px solid", borderColor: "divider", boxShadow: "none" };

const LoadingBlock = () => (
  <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress /></Box>
);

function EmptyBlock({ children }) {
  return (
    <Paper variant="outlined" sx={{ py: 5, px: 2, textAlign: "center", borderRadius: 2, borderStyle: "dashed", bgcolor: "background.paper" }}>
      <Typography variant="body2" color="text.secondary">{children}</Typography>
    </Paper>
  );
}

/** One white filter card per tab: filters wrap on the left, Reset / Refresh on the right. */
function FilterBar({ children, footer, onReset, onRefresh, refreshing }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "center" }}>
        {children}
        <Box sx={{ flex: 1, minWidth: 8 }} />
        <Stack direction="row" spacing={1} alignItems="center">
          {onReset && (
            <Button size="small" startIcon={<RestartAltIcon />} onClick={onReset} sx={{ textTransform: "none", fontWeight: 600 }}>
              Reset filters
            </Button>
          )}
          {onRefresh && (
            <Button
              size="small"
              variant="outlined"
              startIcon={refreshing ? <CircularProgress size={14} /> : <RefreshIcon />}
              onClick={onRefresh}
              disabled={refreshing}
              sx={{ textTransform: "none", fontWeight: 600 }}
            >
              Refresh
            </Button>
          )}
        </Stack>
      </Box>
      {footer && <Box sx={{ mt: 1.5, display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "center" }}>{footer}</Box>}
    </Paper>
  );
}

/** Dates: quick presets plus the from / to fields. `allowAll` adds "All time" (empty range). */
function DateRange({ from, to, onChange, allowAll = true }) {
  const today = localDateIso();
  const presets = [
    { key: "today", label: "Today", from: today, to: today },
    { key: "7", label: "7 days", from: daysAgoIso(7), to: today },
    { key: "30", label: "30 days", from: daysAgoIso(30), to: today },
    ...(allowAll ? [{ key: "all", label: "All time", from: "", to: "" }] : []),
  ];
  const active = presets.find((p) => p.from === (from || "") && p.to === (to || ""))?.key ?? null;
  return (
    <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
      <Typography variant="body2" sx={{ fontWeight: 600, color: "text.secondary" }}>Dates</Typography>
      <ToggleButtonGroup
        exclusive
        size="small"
        color="primary"
        value={active}
        onChange={(_, key) => {
          const p = presets.find((x) => x.key === key);
          if (p) onChange(p.from, p.to);
        }}
      >
        {presets.map((p) => (
          <ToggleButton key={p.key} value={p.key} sx={{ textTransform: "none", px: 1.5, py: 0.5 }}>{p.label}</ToggleButton>
        ))}
      </ToggleButtonGroup>
      <TextField
        size="small"
        type="date"
        label="From"
        InputLabelProps={{ shrink: true }}
        value={from}
        onChange={(e) => onChange(e.target.value, to)}
        sx={{ width: 160 }}
      />
      <TextField
        size="small"
        type="date"
        label="To"
        InputLabelProps={{ shrink: true }}
        value={to}
        onChange={(e) => onChange(from, e.target.value)}
        sx={{ width: 160 }}
      />
    </Stack>
  );
}

const TILE_COLORS = { success: "#15803D", warning: "#B45309", info: "#0369A1", secondary: "#6D28D9", primary: BRAND, default: "#4B5563" };

/** Count tiles; clicking one filters the table. The active tile gets the brand border. */
function SummaryTiles({ tiles, active, onPick }) {
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", sm: "repeat(3, minmax(0, 1fr))", lg: `repeat(${Math.min(tiles.length, 6)}, minmax(0, 1fr))` }, gap: 1.5, mb: 2 }}>
      {tiles.map((t) => {
        const isActive = active === t.key;
        return (
          <Paper
            key={t.key}
            component="button"
            type="button"
            variant="outlined"
            onClick={onPick ? () => onPick(t.key) : undefined}
            aria-pressed={isActive}
            sx={{
              textAlign: "left",
              p: 1.5,
              borderRadius: 2,
              cursor: onPick ? "pointer" : "default",
              font: "inherit",
              bgcolor: "background.paper",
              border: isActive ? `2px solid ${BRAND}` : "1px solid",
              borderColor: isActive ? BRAND : "divider",
              "&:hover": onPick ? { bgcolor: "#FAFAFF" } : undefined,
            }}
          >
            <Typography variant="caption" sx={{ display: "block", fontWeight: 600, color: TILE_COLORS[t.color] ?? TILE_COLORS.default, ...clampSx(1) }}>
              {t.label}
            </Typography>
            <Typography sx={{ fontSize: 22, fontWeight: 800, color: "text.primary", lineHeight: 1.3 }}>{t.value}</Typography>
          </Paper>
        );
      })}
    </Box>
  );
}

/** "Verified" / "Rejected" / "Not yet" for one platform of one item. */
function PlatformPill({ approved = 0, rejected = 0 }) {
  if (rejected > 0) return <StatusChip tone="red" label="Rejected" />;
  if (approved > 0) return <StatusChip tone="green" label="Verified" />;
  return <StatusChip tone="amber" label="Not yet" />;
}

const textOf = (v) => {
  if (v === null || v === undefined) return "";
  if (typeof v === "string") return decodeHtml(v.replace(/<[^>]*>/g, " ")).replace(/\s+/g, " ").trim();
  if (Array.isArray(v)) return v.map(textOf);
  if (typeof v === "object") return Object.fromEntries(Object.keys(v).sort().map((k) => [k, textOf(v[k])]));
  return v;
};

function decodeHtml(s) {
  try {
    const el = document.createElement("textarea");
    el.innerHTML = s;
    return el.value;
  } catch {
    return s.replace(/&nbsp;/g, " ").replace(/&amp;/g, "&").replace(/&lt;/g, "<").replace(/&gt;/g, ">");
  }
}

/** True when every changed field reads the same once HTML tags and spacing are ignored. */
export function isFormattingOnlyEdit(edit) {
  const oldVals = edit?.old || {};
  const newVals = edit?.attributes || {};
  const keys = Array.from(new Set([...Object.keys(oldVals), ...Object.keys(newVals)]));
  if (keys.length === 0) return false;
  return keys.every((k) => JSON.stringify(textOf(oldVals[k])) === JSON.stringify(textOf(newVals[k])));
}

function difficultyChipColor(difficulty) {
  const key = String(difficulty).toLowerCase();
  if (key === "easy") return "success";
  if (key === "hard") return "error";
  return "warning";
}

function PlatformChip({ platform }) {
  if (!platform) return <Typography variant="caption" color="text.disabled">—</Typography>;
  return <Chip label={platform} size="small" variant="outlined" color={PLATFORM_COLORS[platform] || "default"} sx={{ height: 20, fontSize: "0.65rem" }} />;
}

const VERDICT_TONES = { approved: "green", rejected: "red", flagged: "amber" };

function VerdictChip({ verdict }) {
  if (!verdict) return <Typography variant="caption" color="text.disabled">{EMPTY}</Typography>;
  // An unknown status must never look approved.
  return <StatusChip tone={VERDICT_TONES[verdict] ?? "grey"} label={STATUS_CONFIG[verdict]?.label ?? humanize(verdict)} />;
}

function groupVerificationsByTester(verifications = []) {
  const map = new Map();
  verifications.forEach((v) => {
    const key = v.user?.id ?? v.user?.name ?? "unknown";
    if (!map.has(key)) {
      map.set(key, { name: v.user?.name ?? "—", web: null, mobile: null, notes: [] });
    }
    const row = map.get(key);
    const platform = v.platform || "web";
    row[platform] = v;
    if (v.notes) row.notes.push(v.notes);
  });
  return Array.from(map.values());
}

function matchesCoverageFilter(item, filterCoverage) {
  if (!filterCoverage) return true;

  const webApproved = item.web_approved ?? 0;
  const webRejected = item.web_rejected ?? 0;
  const mobileApproved = item.mobile_approved ?? 0;
  const mobileRejected = item.mobile_rejected ?? 0;
  const webTotal = webApproved + webRejected;
  const mobileTotal = mobileApproved + mobileRejected;

  switch (filterCoverage) {
    case "both":
      return webApproved > 0 && mobileApproved > 0;
    case "web_only":
      return webApproved > 0 && mobileTotal === 0;
    case "mobile_only":
      return mobileTotal > 0 && webTotal === 0;
    case "none":
      return webApproved === 0 && mobileApproved === 0;
    case "has_rejected":
      return webRejected > 0 || mobileRejected > 0;
    default:
      return true;
  }
}

function matchesVerdictFilter(item, filterVerdict) {
  if (!filterVerdict) return true;
  const approved = item.approved ?? 0;
  const rejected = item.rejected ?? 0;
  switch (filterVerdict) {
    case "approved":
      return approved > 0 && rejected === 0;
    case "rejected":
      return rejected > 0 && approved === 0;
    case "mixed":
      return approved > 0 && rejected > 0;
    default:
      return true;
  }
}

function computeCoverageSummary(items = []) {
  let bothVerified = 0;
  let webOnly = 0;
  let mobileOnly = 0;
  let unverified = 0;

  items.forEach((item) => {
    const webApproved = item.web_approved ?? 0;
    const mobileApproved = item.mobile_approved ?? 0;
    const webTotal = (item.web_approved ?? 0) + (item.web_rejected ?? 0);
    const mobileTotal = (item.mobile_approved ?? 0) + (item.mobile_rejected ?? 0);

    if (webApproved > 0 && mobileApproved > 0) bothVerified += 1;
    else if (webApproved > 0 && mobileTotal === 0) webOnly += 1;
    else if (mobileTotal > 0 && webTotal === 0) mobileOnly += 1;
    else if (webApproved === 0 && mobileApproved === 0) unverified += 1;
  });

  return { total: items.length, bothVerified, webOnly, mobileOnly, unverified };
}

const EDIT_HTML_FIELDS = new Set(["question_text", "explanation", "correct_answer", "correct_answer_text", "section_instructions"]);

function ImageThumb({ url }) {
  const [err, setErr] = useState(false);
  if (!url || err) return <Chip label="[img]" size="small" variant="outlined" sx={{ fontSize: "0.6rem", height: 18 }} />;
  return (
    <Box
      component="img"
      src={url}
      alt=""
      onError={() => setErr(true)}
      sx={{ height: 48, maxWidth: 80, objectFit: "contain", borderRadius: 1, border: "1px solid", borderColor: "divider", cursor: "pointer" }}
      onClick={() => window.open(url, "_blank")}
    />
  );
}

function SubQuestionsValue({ value }) {
  if (!Array.isArray(value) || value.length === 0)
    return <Typography variant="caption" color="text.disabled">— (empty)</Typography>;
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
      {value.map((sq, i) => (
        <Box key={i} sx={{ p: 1, borderRadius: 1, border: "1px solid", borderColor: "divider", bgcolor: "background.paper" }}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary", display: "block", mb: 0.25 }}>
            Part {i + 1}{sq.type ? ` — ${sq.type}` : ""}
          </Typography>
          {sq.question_text && (
            <Box sx={{ "& p": { m: 0 }, fontSize: "0.8rem" }}>
              <HtmlWithLatex html={sq.question_text} />
            </Box>
          )}
          {sq.answer?.correct_answer != null && sq.answer.correct_answer !== "" && (
            <Typography variant="caption" sx={{ color: "success.dark", fontWeight: 600, display: "block", mt: 0.25 }}>
              Answer: {stripHtml(String(sq.answer.correct_answer))}
            </Typography>
          )}
          {sq.answer?.correct_answer_text && (
            <Typography variant="caption" sx={{ color: "success.dark", fontWeight: 600, display: "block", mt: 0.25 }}>
              Answer: {stripHtml(sq.answer.correct_answer_text)}
            </Typography>
          )}
          {sq.answer?.correct_blanks?.length > 0 && (
            <Stack direction="row" spacing={0.5} sx={{ mt: 0.25, flexWrap: "wrap" }}>
              {sq.answer.correct_blanks.map((b, bi) => (
                <Chip key={bi} label={Array.isArray(b) ? b[0] : b} size="small" color="success" variant="outlined" sx={{ height: 16, fontSize: "0.58rem" }} />
              ))}
            </Stack>
          )}
          {sq.options?.length > 0 && (
            <Box component="ol" sx={{ m: 0, pl: 2, mt: 0.25 }}>
              {sq.options.map((o, oi) => (
                <li key={oi} style={{ fontSize: "0.75rem" }}>
                  {o?.image_url ? <ImageThumb url={o.image_url} /> : (o?.option_text ?? o?.text ?? "")}
                  {o?.is_correct && <Chip label="✓" size="small" color="success" sx={{ ml: 0.5, height: 14, fontSize: "0.55rem" }} />}
                </li>
              ))}
            </Box>
          )}
        </Box>
      ))}
    </Box>
  );
}

function ImageUrlsValue({ value }) {
  if (!value || (Array.isArray(value) && value.length === 0))
    return <Typography variant="caption" color="text.disabled">— (none)</Typography>;
  const urls = Array.isArray(value) ? value : [value];
  return (
    <Stack direction="row" spacing={0.75} flexWrap="wrap">
      {urls.map((u, i) => <ImageThumb key={i} url={u} />)}
    </Stack>
  );
}

function AnswerValue({ value }) {
  const answerHtml = value?.correct_answer_text || value?.correct_answer || "";
  const blanks = Array.isArray(value?.correct_blanks) ? value.correct_blanks : [];
  const options = Array.isArray(value?.correct_options) ? value.correct_options : [];

  if (!answerHtml && blanks.length === 0 && options.length === 0) {
    return <Typography component="span" variant="caption" color="text.disabled">— (no answer)</Typography>;
  }
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
      {answerHtml && <Box sx={{ "& p": { m: 0 } }}><HtmlWithLatex html={String(answerHtml)} /></Box>}
      {blanks.length > 0 && (
        <Stack direction="row" spacing={0.5} flexWrap="wrap">
          {blanks.map((b, i) => <Chip key={i} label={Array.isArray(b) ? b[0] : b} size="small" color="success" variant="outlined" sx={{ height: 18, fontSize: "0.65rem" }} />)}
        </Stack>
      )}
      {options.length > 0 && (
        <Stack direction="row" spacing={0.5} flexWrap="wrap">
          {options.map((o, i) => <Chip key={i} label={String(o)} size="small" color="success" variant="outlined" sx={{ height: 18, fontSize: "0.65rem" }} />)}
        </Stack>
      )}
    </Box>
  );
}

function EditFieldValue({ field, value }) {
  if (value === undefined || value === null || value === "") {
    return <Typography component="span" variant="caption" color="text.disabled">—</Typography>;
  }
  if (EDIT_HTML_FIELDS.has(field) && typeof value === "string") {
    return <Box sx={{ "& p": { m: 0 } }}><HtmlWithLatex html={value} /></Box>;
  }
  if ((field === "answer" || field === "answer_data") && typeof value === "object") {
    return <AnswerValue value={value} />;
  }
  if (field === "sub_questions") {
    return <SubQuestionsValue value={value} />;
  }
  if (field === "image_url" || field === "image_urls") {
    return <ImageUrlsValue value={value} />;
  }
  if (field === "options" && Array.isArray(value)) {
    if (value.length === 0) return <Typography variant="caption" color="text.disabled">— (empty)</Typography>;
    return (
      <Box component="ol" sx={{ m: 0, pl: 2 }}>
        {value.map((o, i) => (
          <li key={i} style={{ marginBottom: 2 }}>
            {o?.image_url
              ? <ImageThumb url={o.image_url} />
              : <Typography component="span" variant="body2">{o?.option_text ?? o?.text ?? ""}</Typography>}
            {o?.is_correct && <Chip label="correct" size="small" color="success" variant="outlined" sx={{ ml: 0.5, height: 16, fontSize: "0.58rem" }} />}
          </li>
        ))}
      </Box>
    );
  }
  if (field === "correct_blanks" && Array.isArray(value)) {
    return (
      <Stack direction="row" spacing={0.5} flexWrap="wrap">
        {value.map((b, i) => <Chip key={i} label={Array.isArray(b) ? b[0] : b} size="small" color="success" variant="outlined" sx={{ height: 18, fontSize: "0.65rem" }} />)}
      </Stack>
    );
  }
  if (typeof value === "boolean") {
    return <Chip label={value ? "Yes" : "No"} size="small" color={value ? "success" : "default"} variant="outlined" sx={{ height: 18, fontSize: "0.65rem" }} />;
  }
  if (typeof value === "object") {
    return <Box component="pre" sx={{ m: 0, fontSize: 11, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{JSON.stringify(value, null, 1)}</Box>;
  }
  return <Typography component="span" variant="body2">{String(value)}</Typography>;
}

function EditDiffRow({ edit }) {
  const [expanded, setExpanded] = useState(false);
  const oldVals = edit.old || {};
  const newVals = edit.attributes || {};
  const keys = Array.from(new Set([...Object.keys(oldVals), ...Object.keys(newVals)]));
  const changeCount = keys.length;

  return (
    <>
      <TableRow hover>
        <TableCell>
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: "0.875rem" }}>{edit.user?.name ?? "—"}</Typography>
            {edit.user?.email && (
              <Typography variant="caption" color="text.secondary">{edit.user.email}</Typography>
            )}
          </Box>
        </TableCell>
        <TableCell sx={{ fontFamily: "monospace", fontWeight: 600 }}>#{edit.subject_id}</TableCell>
        <TableCell>
          <Stack spacing={0.5}>
            <Stack direction="row" spacing={0.5} flexWrap="wrap">
              {keys.map((k) => (
                <Chip key={k} label={fieldLabel(k)} size="small" variant="outlined" sx={{ height: 20, fontSize: "0.62rem" }} />
              ))}
            </Stack>
            <Stack direction="row" spacing={0.75} alignItems="center">
              <Typography variant="caption" color="text.secondary">
                {changeCount} field{changeCount !== 1 ? "s" : ""} changed
              </Typography>
              {isFormattingOnlyEdit(edit) && <StatusChip tone="grey" label="Formatting only" sx={{ fontSize: 11, py: "1px" }} />}
            </Stack>
          </Stack>
        </TableCell>
        <TableCell><PlatformChip platform={edit.platform} /></TableCell>
        <TableCell>
          <Typography variant="body2" sx={{ whiteSpace: "nowrap" }}>{formatDateTime(edit.verified_at)}</Typography>
        </TableCell>
        <TableCell align="right">
          <Tooltip title={expanded ? "Hide before / after" : "Show before / after"}>
            <IconButton size="small" onClick={() => setExpanded((p) => !p)} aria-label={expanded ? "Hide before and after" : "Show before and after"}>
              {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={6} sx={{ py: 0, borderBottom: expanded ? undefined : "none" }}>
          <Collapse in={expanded} unmountOnExit>
            <Box sx={{ p: 1.5, my: 0.5, bgcolor: "#FAFAFF", borderRadius: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ py: 0.5, width: 160 }}>Field</TableCell>
                    <TableCell sx={{ py: 0.5, color: "#B91C1C !important" }}>Before</TableCell>
                    <TableCell sx={{ py: 0.5, color: "#15803D !important" }}>After</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {keys.map((k) => (
                    <TableRow key={k}>
                      <TableCell sx={{ verticalAlign: "top", fontWeight: 600, whiteSpace: "nowrap" }}>
                        {fieldLabel(k)}
                        <Typography variant="caption" display="block" color="text.disabled">{k}</Typography>
                      </TableCell>
                      <TableCell sx={{ verticalAlign: "top", bgcolor: "#FEF2F2", wordBreak: "break-word" }}>
                        <EditFieldValue field={k} value={oldVals[k]} />
                      </TableCell>
                      <TableCell sx={{ verticalAlign: "top", bgcolor: "#F0FDF4", wordBreak: "break-word" }}>
                        <EditFieldValue field={k} value={newVals[k]} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

function TesterEditsPanel({ edits, loading, error, onRetry, hideFormatting }) {
  if (loading && !(edits?.length)) return <LoadingBlock />;
  if (error) return <LoadError what="tester edits" error={error} onRetry={onRetry} />;
  if (!edits || edits.length === 0) return <EmptyBlock>No question edits recorded for the selected filters.</EmptyBlock>;

  const shown = hideFormatting ? edits.filter((e) => !isFormattingOnlyEdit(e)) : edits;
  const hidden = edits.length - shown.length;

  return (
    <>
      {hidden > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
          {hidden} formatting-only edit{hidden !== 1 ? "s" : ""} hidden on this page (only HTML tags or spacing changed).
        </Typography>
      )}
      {shown.length === 0 ? (
        <EmptyBlock>Every edit on this page only changed formatting. Untick "Hide formatting-only edits" to see them.</EmptyBlock>
      ) : (
        <TableContainer component={Paper} sx={{ ...TABLE_SX, opacity: loading ? 0.55 : 1, transition: "opacity .15s" }}>
          {loading && <LinearProgress />}
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Tester</TableCell>
                <TableCell>Question</TableCell>
                <TableCell>Fields changed</TableCell>
                <TableCell>Platform</TableCell>
                <TableCell>When</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {shown.map((e) => <EditDiffRow key={e.id} edit={e} />)}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </>
  );
}

const typeColors = {
  "multiple-choice": "#1976d2",
  "multiple-select": "#7b1fa2",
  "true-false": "#388e3c",
  "fill-in-the-blank": "#f57c00",
  "multi-fill": "#e64a19",
  matching: "#0097a7",
  ordering: "#5d4037",
  essay: "#c2185b",
  "short-answer": "#455a64",
  grouped: "#6d4c41",
};

const typeLabel = (t) => {
  const map = {
    "multiple-choice": "MCQ",
    "multiple-select": "Multi-select",
    "true-false": "True/False",
    "fill-in-the-blank": "Fill blank",
    "multi-fill": "Multi-fill",
    matching: "Matching",
    ordering: "Ordering",
    essay: "Essay",
    "short-answer": "Short ans.",
    grouped: "Grouped",
  };
  return map[t] || t;
};

export function AggregateChips({ aggregate }) {
  if (!aggregate || aggregate.total === 0) return <Typography variant="caption" color="text.disabled">—</Typography>;
  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap">
      {aggregate.approved > 0 && <Chip icon={<CheckCircleOutlineIcon />} label={aggregate.approved} color="success" size="small" variant="outlined" />}
      {aggregate.rejected > 0 && <Chip icon={<CancelOutlinedIcon />} label={aggregate.rejected} color="error" size="small" variant="outlined" />}
      {aggregate.flagged > 0 && <Chip label={`⚑ ${aggregate.flagged}`} color="warning" size="small" variant="outlined" />}
    </Stack>
  );
}

// ── Individual-record row ─────────────────────────────────────────────────────

function VerificationRow({ row }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <>
      <TableRow hover>
        <TableCell><TypeChip type={row.verifiable_type} /></TableCell>
        <TableCell sx={{ fontFamily: "monospace", fontWeight: 700 }}>#{row.verifiable_id}</TableCell>
        <TableCell sx={{ fontWeight: 600 }}>{row.user?.name ?? `User #${row.user_id}`}</TableCell>
        <TableCell><VerdictChip verdict={row.status || "unknown"} /></TableCell>
        <TableCell><PlatformChip platform={row.platform} /></TableCell>
        <TableCell sx={{ maxWidth: 320 }}>
          {row.notes ? (
            <Tooltip title={row.notes} placement="bottom-start">
              <Typography variant="body2" sx={clampSx(2)}>{row.notes}</Typography>
            </Tooltip>
          ) : (
            <Typography variant="caption" color="text.disabled">{EMPTY}</Typography>
          )}
        </TableCell>
        <TableCell sx={{ whiteSpace: "nowrap" }}>{formatDateTime(row.verified_at)}</TableCell>
        <TableCell align="right">
          {row.notes && (
            <Tooltip title={expanded ? "Hide notes" : "Show full notes"}>
              <IconButton size="small" onClick={() => setExpanded((p) => !p)} aria-label={expanded ? "Hide notes" : "Show full notes"}>
                {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          )}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={8} sx={{ py: 0, borderBottom: expanded ? undefined : "none" }}>
          <Collapse in={expanded} unmountOnExit>
            <Box sx={{ p: 2, bgcolor: "#FAFAFF", borderRadius: 1, my: 0.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Full Notes</Typography>
              <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: "pre-wrap" }}>{row.notes || "No notes provided."}</Typography>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

// ── Grouped-by-item row ───────────────────────────────────────────────────────

const decodeSnippet = (s) => {
  if (!s) return '';
  try {
    const el = document.createElement('textarea');
    el.innerHTML = s;
    return el.value;
  } catch {
    return s.replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>');
  }
};

function ItemAggregateRow({ item, onView, onEdit, onVerify }) {
  const [expanded, setExpanded] = useState(false);
  const testerRows = groupVerificationsByTester(item.verifications);

  return (
    <>
      <TableRow hover>
        <TableCell>
          <Stack spacing={0.5} alignItems="flex-start">
            <TypeChip type={item.verifiable_type} />
            {item.question_data?.type && (
              <Chip
                label={typeLabel(item.question_data.type)}
                size="small"
                sx={{
                  fontSize: "0.62rem",
                  height: 18,
                  bgcolor: (typeColors[item.question_data.type] || "#555") + "18",
                  color: typeColors[item.question_data.type] || "#555",
                  fontWeight: 600,
                  border: "none",
                }}
              />
            )}
          </Stack>
        </TableCell>
        <TableCell>
          <Stack spacing={0.3}>
            <Typography sx={{ fontFamily: "monospace", fontWeight: 700 }}>#{item.verifiable_id}</Typography>
            {item.question_data?.text_snippet && (
              <Tooltip title={decodeSnippet(item.question_data.text_snippet)} placement="bottom-start">
                <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 320, ...clampSx(2) }}>
                  {decodeSnippet(item.question_data.text_snippet)}
                </Typography>
              </Tooltip>
            )}
          </Stack>
        </TableCell>
        <TableCell>
          <AggregateChips aggregate={{ approved: item.approved, rejected: item.rejected, total: item.total }} />
        </TableCell>
        <TableCell>
          <PlatformPill approved={item.web_approved ?? 0} rejected={item.web_rejected ?? 0} />
        </TableCell>
        <TableCell>
          <PlatformPill approved={item.mobile_approved ?? 0} rejected={item.mobile_rejected ?? 0} />
        </TableCell>
        <TableCell sx={{ whiteSpace: "nowrap" }}>
          {item.total} tester{item.total !== 1 ? "s" : ""}
        </TableCell>
        <TableCell sx={{ whiteSpace: "nowrap" }}>{formatDate(item.last_verified_at)}</TableCell>
        <TableCell align="right">
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 0.25 }}>
            {item.verifiable_type === "question" && (
              <RowActions
                visible={3}
                actions={[
                  { label: "Accept / reject", icon: <FactCheckOutlinedIcon fontSize="small" />, onClick: () => onVerify?.(item) },
                  { label: "View question", icon: <VisibilityIcon fontSize="small" />, onClick: () => onView?.(item.verifiable_id) },
                  { label: "Edit question", icon: <EditIcon fontSize="small" />, onClick: () => onEdit?.(item.verifiable_id) },
                ]}
              />
            )}
            <Tooltip title={expanded ? "Hide tester verdicts" : "Show tester verdicts"}>
              <IconButton size="small" onClick={() => setExpanded((p) => !p)} aria-label={expanded ? "Hide tester verdicts" : "Show tester verdicts"}>
                {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          </Box>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={8} sx={{ py: 0, borderBottom: expanded ? undefined : "none" }}>
          <Collapse in={expanded} unmountOnExit>
            <Box sx={{ p: 1.5, my: 0.5, bgcolor: "#FAFAFF", borderRadius: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary", display: "block", mb: 1 }}>
                Tester verdicts
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, py: 0.5 }}>Tester</TableCell>
                    <TableCell sx={{ fontWeight: 700, py: 0.5 }}>Web</TableCell>
                    <TableCell sx={{ fontWeight: 700, py: 0.5 }}>Mobile</TableCell>
                    <TableCell sx={{ fontWeight: 700, py: 0.5 }}>Notes</TableCell>
                    <TableCell sx={{ fontWeight: 700, py: 0.5 }}>Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {testerRows.map((row) => {
                    const latest = [row.web, row.mobile]
                      .filter(Boolean)
                      .sort((a, b) => new Date(b.verified_at) - new Date(a.verified_at))[0];
                    return (
                      <TableRow key={row.name}>
                        <TableCell sx={{ py: 0.75, fontWeight: 600 }}>{row.name}</TableCell>
                        <TableCell sx={{ py: 0.75 }}>
                          <VerdictChip verdict={row.web?.status} />
                        </TableCell>
                        <TableCell sx={{ py: 0.75 }}>
                          <VerdictChip verdict={row.mobile?.status} />
                        </TableCell>
                        <TableCell sx={{ py: 0.75, maxWidth: 320, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                          {row.notes.length > 0
                            ? <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>{row.notes.join("\n")}</Typography>
                            : <Typography variant="caption" color="text.disabled">—</Typography>
                          }
                        </TableCell>
                        <TableCell sx={{ py: 0.75 }}>
                          {formatDateTime(latest?.verified_at)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

// ── Paper coverage panel ──────────────────────────────────────────────────────

function CoverageBar({ verified, total }) {
  const pct = total > 0 ? Math.min(100, Math.round((verified / total) * 100)) : 0;
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 160 }}>
      <Box sx={{ flex: 1, height: 8, borderRadius: 4, bgcolor: "action.hover", overflow: "hidden" }}>
        <Box sx={{ width: `${pct}%`, height: "100%", bgcolor: pct >= 100 ? "success.main" : "primary.main", transition: "width .2s" }} />
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 34, textAlign: "right" }}>{pct}%</Typography>
    </Box>
  );
}

// Per-platform coverage under the Verified/Total figure — makes a web-only paper obvious instead
// of letting it read as fully verified.
function PlatformCoverage({ row }) {
  const platforms = row.required_platforms ?? ["web", "mobile"];
  if (platforms.length < 2) return null;
  const verified = row.platform_verified ?? {};
  const pending = row.platform_pending ?? {};
  return (
    <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
      {platforms.map((pf) => (
        <Chip
          key={pf}
          label={`${pf}: ${verified[pf] ?? 0}${pending[pf] ? ` (${pending[pf]} pending)` : ""}`}
          size="small"
          variant="outlined"
          color={pending[pf] ? "warning" : "default"}
          sx={{ height: 18, fontSize: "0.6rem" }}
        />
      ))}
    </Stack>
  );
}

const fmtCount = fmtNum;

const VERIFIER_SECTIONS = [
  { key: "papers", label: "Papers", tab: "papers" },
  { key: "mock_exams", label: "Mock exams", tab: "mock-exams" },
  { key: "worksheets", label: "Worksheets", tab: "worksheets" },
];

// "Verified by" summary: what the verifier chosen in the filter verified, per section.
// Counts questions with at least one verdict by that verifier (on the chosen platform, or any),
// so a web-only verifier such as Grokbot QA shows up even though both platforms are in scope.
export function VerifierSummaryCard({ summary, loading, error, onOpenSection }) {
  if (error) return <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>;
  if (!summary) {
    return loading ? (
      <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2, display: "flex", alignItems: "center", gap: 1 }}>
        <CircularProgress size={16} /> <Typography variant="body2">Counting verified questions…</Typography>
      </Paper>
    ) : null;
  }
  const q = summary.questions ?? {};
  const platformLabel = summary.platform ? ` on ${summary.platform}` : " (any platform)";
  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }} data-testid="verifier-summary">
      <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
        <VerifiedIcon color="primary" fontSize="small" />
        <Typography sx={{ fontWeight: 700 }}>
          Verified by {summary.verifier?.name ?? "—"}{platformLabel}
        </Typography>
        <Chip label={`${fmtCount(q.questions)} questions`} color="primary" size="small" />
        {q.approved > 0 && <Chip icon={<CheckCircleOutlineIcon />} label={`${fmtCount(q.approved)} approved`} color="success" size="small" variant="outlined" />}
        {q.rejected > 0 && <Chip icon={<CancelOutlinedIcon />} label={`${fmtCount(q.rejected)} rejected`} color="error" size="small" variant="outlined" />}
        {q.flagged > 0 && <Chip label={`${fmtCount(q.flagged)} flagged`} color="warning" size="small" variant="outlined" />}
        {loading && <CircularProgress size={14} />}
      </Stack>
      <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
        {VERIFIER_SECTIONS.map(({ key, label, tab }) => {
          const sec = summary.sections?.[key];
          if (!sec) return null;
          return (
            <Paper
              key={key}
              variant="outlined"
              onClick={() => onOpenSection?.(tab)}
              data-testid={`verifier-summary-${key}`}
              sx={{ p: 1.5, flex: "1 1 240px", cursor: onOpenSection ? "pointer" : "default", "&:hover": { bgcolor: "action.hover" } }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: "uppercase" }}>{label}</Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
                {fmtCount(sec.questions_verified)}
                <Typography component="span" variant="body2" color="text.secondary"> / {fmtCount(sec.questions_total)} questions</Typography>
              </Typography>
              <CoverageBar verified={sec.questions_verified} total={sec.questions_total} />
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                Every question verified in {fmtCount(sec.items_all_verified)} of {fmtCount(sec.items)} · some in {fmtCount(sec.items_with_verifier)} · none in {fmtCount(sec.items_none_verified)}
              </Typography>
            </Paper>
          );
        })}
      </Box>
    </Paper>
  );
}

function PaperCoveragePanel({ papers, loading, error, onRetry, onViewPaper, onOpenQuestion, openingQuestion, entityLabel = "paper", verifierName = null }) {
  const [expanded, setExpanded] = useState(null);
  const entityTitle = entityLabel.replace(/\b\w/g, (c) => c.toUpperCase());
  const showVerifier = Boolean(verifierName) && (papers ?? []).some((p) => p.verifier_verified_questions !== undefined);
  const colCount = showVerifier ? 8 : 7;

  if (loading && !(papers?.length)) return <LoadingBlock />;
  if (error) return <LoadError what={`${entityLabel} coverage`} error={error} onRetry={onRetry} />;
  if (!papers || papers.length === 0) return <EmptyBlock>No {entityLabel}s found for the selected filters.</EmptyBlock>;

  return (
    <TableContainer component={Paper} sx={{ ...TABLE_SX, opacity: loading ? 0.55 : 1, transition: "opacity .15s" }}>
      {loading && <LinearProgress />}
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{entityTitle}</TableCell>
            <TableCell>Coverage</TableCell>
            <TableCell>Verified / total</TableCell>
            {showVerifier && <TableCell>Verified by {verifierName}</TableCell>}
            <TableCell>Verdicts</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Checked by</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {papers.map((p) => {
            const isOpen = expanded === p.paper_id;
            return (
              <React.Fragment key={p.paper_id}>
                <TableRow hover>
                  <TableCell sx={{ maxWidth: 380 }}>
                    <Typography sx={{ fontWeight: 600, fontSize: "0.875rem" }}>{p.paper_name}</Typography>
                    {entityLabel === "mock exam" && (
                      <Chip label="Mock exam" size="small" color="primary" variant="outlined" sx={{ mt: 0.25, height: 20 }} />
                    )}
                    {entityLabel === "paper" && (
                      <Chip
                        label={p.shelf === "practice" ? "Practice paper" : "Past paper"}
                        size="small"
                        color={p.shelf === "practice" ? "secondary" : "default"}
                        variant="outlined"
                        sx={{ mt: 0.25, height: 20 }}
                      />
                    )}
                    <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
                      <Typography variant="caption" color="text.disabled" sx={{ fontFamily: "monospace" }}>#{p.paper_id}</Typography>
                      {entityLabel === "worksheet" && p.difficulty && (
                        <Chip
                          label={p.difficulty}
                          size="small"
                          variant="outlined"
                          color={difficultyChipColor(p.difficulty)}
                          sx={{ height: 18, fontSize: "0.65rem" }}
                        />
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell><CoverageBar verified={p.verified_questions} total={p.total_questions} /></TableCell>
                  <TableCell>
                    <Tooltip title="Verified on every platform in scope — a web-only verdict does not count">
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {p.verified_questions} / {p.total_questions}
                      </Typography>
                    </Tooltip>
                    <PlatformCoverage row={p} />
                  </TableCell>
                  {showVerifier && (
                    <TableCell data-testid="verifier-cell">
                      <Tooltip title={`Questions in this ${entityLabel} with a verdict by ${verifierName}`}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {p.verifier_verified_questions ?? 0} / {p.total_questions}
                        </Typography>
                      </Tooltip>
                      {p.verifier_all_verified
                        ? <Chip icon={<CheckCircleOutlineIcon />} label="All" color="success" size="small" variant="outlined" sx={{ mt: 0.5, height: 20 }} />
                        : (p.verifier_verified_questions ?? 0) === 0
                          ? <Chip label="None" size="small" variant="outlined" sx={{ mt: 0.5, height: 20 }} />
                          : null}
                    </TableCell>
                  )}
                  <TableCell>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap">
                      {p.approved_questions > 0 && <Chip icon={<CheckCircleOutlineIcon />} label={p.approved_questions} color="success" size="small" variant="outlined" />}
                      {p.rejected_questions > 0 && <Chip icon={<CancelOutlinedIcon />} label={p.rejected_questions} color="error" size="small" variant="outlined" />}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                      {p.fully_verified
                        ? <StatusChip tone="green" label="Fully verified" />
                        : (p.verified_questions > 0 || p.partly_verified_questions > 0)
                          ? <StatusChip tone="amber" label="Partial" />
                          : <StatusChip tone="grey" label="Not started" />}
                      {p.paper_approved && (
                        <Tooltip title={`Signed off by ${(p.paper_sign_offs ?? []).map((s) => s.user?.name).filter(Boolean).join(", ") || "a tester"}`}>
                          <span><StatusChip tone="green" label="Signed off" /></span>
                        </Tooltip>
                      )}
                      {!p.paper_approved && (p.paper_approved_platforms?.length ?? 0) > 0 && (
                        <StatusChip tone="amber" label={`Signed off on ${p.paper_approved_platforms.join(", ")} only`} />
                      )}
                      {p.paper_rejected && <StatusChip tone="red" label={`${entityTitle} rejected`} />}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    {p.grok_verified_questions !== undefined ? (
                      <Stack spacing={0.5} alignItems="flex-start">
                        <Chip
                          label={(p.grok_verified_questions ?? 0) > 0 ? `Grok QA ${p.grok_verified_questions}` : "Grok QA —"}
                          size="small"
                          color={(p.grok_verified_questions ?? 0) > 0 ? "secondary" : "default"}
                          variant="outlined"
                          sx={{ height: 20 }}
                        />
                        {(p.manual_verifiers ?? []).length > 0 ? (
                          (p.manual_verifiers ?? []).map((v) => (
                            <Chip
                              key={v.name}
                              label={`${v.name} ${v.verified_count}`}
                              size="small"
                              color="primary"
                              variant="outlined"
                              sx={{ height: 20 }}
                            />
                          ))
                        ) : (
                          <Chip label="Manual —" size="small" variant="outlined" sx={{ height: 20 }} />
                        )}
                      </Stack>
                    ) : (
                      <Chip label={`${p.verifiers?.length ?? 0} tester${(p.verifiers?.length ?? 0) !== 1 ? "s" : ""}`} size="small" variant="outlined" color="primary" />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 0.25 }}>
                      {onViewPaper && (
                        <RowActions
                          visible={1}
                          actions={[{ label: `Open this ${entityLabel}'s questions in Question Bank`, icon: <FilterListIcon fontSize="small" />, onClick: () => onViewPaper(p.paper_id, p) }]}
                        />
                      )}
                      <Tooltip title={isOpen ? "Hide details" : "Show unverified questions and testers"}>
                        <IconButton size="small" onClick={() => setExpanded(isOpen ? null : p.paper_id)} aria-label={isOpen ? "Hide details" : "Show details"}>
                          {isOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell colSpan={colCount} sx={{ py: 0, borderBottom: isOpen ? undefined : "none" }}>
                    <Collapse in={isOpen} unmountOnExit>
                      <Box sx={{ p: 1.5, my: 0.5, bgcolor: "#FAFAFF", borderRadius: 1 }}>
                        {p.paper_sign_offs?.length > 0 && (
                          <Box sx={{ mb: 1.5 }}>
                            <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary", display: "block", mb: 0.5 }}>
                              Whole-{entityLabel} sign-off
                            </Typography>
                            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                              {p.paper_sign_offs.map((s, i) => (
                                <Chip
                                  key={s.user?.id ?? i}
                                  icon={s.status === "approved" ? <VerifiedIcon /> : <CancelOutlinedIcon />}
                                  label={`${s.user?.name ?? "—"} · ${s.status}${s.verified_at ? ` · ${formatDateTime(s.verified_at)}` : ""}`}
                                  size="small"
                                  color={s.status === "approved" ? "success" : "error"}
                                  variant="outlined"
                                />
                              ))}
                            </Stack>
                          </Box>
                        )}
                        {p.unverified_questions?.length > 0 && (
                          <Box sx={{ mb: 1.5 }}>
                            <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary", display: "block", mb: 0.5 }}>
                              Not verified yet ({p.unverified_questions.length}) — click a number to open it for verification.
                              The platform tag is what is still missing.
                            </Typography>
                            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                              {p.unverified_questions.map((q) => {
                                const missing = q.missing ?? p.required_platforms ?? [];
                                const partial = missing.length === 1 && (p.required_platforms ?? ["web", "mobile"]).length > 1;
                                return (
                                <Chip
                                  key={q.id}
                                  label={`${q.number ? `Q${q.number}` : `#${q.id}`}${partial ? ` · ${missing[0]}` : ""}`}
                                  size="small"
                                  color="warning"
                                  variant={partial ? "filled" : "outlined"}
                                  disabled={openingQuestion === q.id}
                                  onClick={() => onOpenQuestion?.(p.paper_id, q)}
                                  sx={{ cursor: "pointer", fontWeight: 700 }}
                                />
                                );
                              })}
                            </Stack>
                          </Box>
                        )}
                        <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary", display: "block", mb: 1 }}>
                          Questions verified per tester
                        </Typography>
                        {p.verifiers?.length > 0 ? (
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 700, py: 0.5 }}>Tester</TableCell>
                                <TableCell sx={{ fontWeight: 700, py: 0.5 }}>Questions Verified</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {p.verifiers.map((v, i) => (
                                <TableRow key={v.user?.id ?? i}>
                                  <TableCell sx={{ py: 0.75, fontWeight: 600 }}>{v.user?.name ?? "—"}</TableCell>
                                  <TableCell sx={{ py: 0.75 }}>
                                    <Chip label={v.verified_count} size="small" color="primary" variant="outlined" />
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        ) : (
                          <Typography variant="body2" color="text.secondary">No tester breakdown available.</Typography>
                        )}
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// ── Tester stats panel ────────────────────────────────────────────────────────

const QB_FILTER_KEY = "qb_list_filters";

function ApprovedShare({ approved = 0, total = 0 }) {
  const pct = total > 0 ? (approved / total) * 100 : 0;
  const label = total > 0 ? `${pct >= 99.95 ? "100" : pct.toFixed(1)}%` : EMPTY;
  return (
    <Tooltip title={`${fmtNum(approved)} of ${fmtNum(total)} verdicts approved`}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 140 }}>
        <Box sx={{ flex: 1, height: 8, borderRadius: 4, bgcolor: total > 0 ? "#FEE2E2" : "action.hover", overflow: "hidden" }}>
          <Box sx={{ width: `${pct}%`, height: "100%", bgcolor: "#15803D" }} />
        </Box>
        <Typography variant="caption" sx={{ minWidth: 40, textAlign: "right" }}>{label}</Typography>
      </Box>
    </Tooltip>
  );
}

function TesterStatsPanel({ stats, loading, error, onRetry, onShowItems }) {
  const [expanded, setExpanded] = useState(null);
  const navigate = useNavigate();

  const handleViewQuestions = (userId) => {
    try {
      const existing = JSON.parse(sessionStorage.getItem(QB_FILTER_KEY) || "{}");
      sessionStorage.setItem(QB_FILTER_KEY, JSON.stringify({ ...existing, filterTesterUserId: String(userId), page: 0 }));
    } catch {}
    navigate("/admin/question-bank");
  };

  if (loading && !(stats?.length)) return <LoadingBlock />;
  if (error) return <LoadError what="tester stats" error={error} onRetry={onRetry} />;
  if (!stats || stats.length === 0) return <EmptyBlock>No verification data found for the selected filters.</EmptyBlock>;

  return (
    <TableContainer component={Paper} sx={{ ...TABLE_SX, opacity: loading ? 0.55 : 1, transition: "opacity .15s" }}>
      {loading && <LinearProgress />}
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Tester</TableCell>
            <TableCell>Approved share</TableCell>
            <TableCell align="right">Approved</TableCell>
            <TableCell align="right">Rejected</TableCell>
            <TableCell align="right">Flagged</TableCell>
            <TableCell align="right">Total verdicts</TableCell>
            <TableCell align="right">Unique items</TableCell>
            <TableCell>Web · Mobile</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {stats.map((s, idx) => {
            const key = s.user?.id ?? idx;
            const isOpen = expanded === key;
            return (
              <React.Fragment key={key}>
                <TableRow hover>
                  <TableCell>
                    <Box>
                      <Typography sx={{ fontWeight: 700, fontSize: "0.875rem" }}>{s.user?.name ?? EMPTY}</Typography>
                      {s.user?.email && <Typography variant="caption" color="text.secondary">{s.user.email}</Typography>}
                    </Box>
                  </TableCell>
                  <TableCell><ApprovedShare approved={s.approved ?? 0} total={s.total ?? 0} /></TableCell>
                  <TableCell align="right">{s.approved > 0 ? fmtNum(s.approved) : EMPTY}</TableCell>
                  <TableCell align="right" sx={s.rejected > 0 ? { color: "#B91C1C", fontWeight: 700 } : { color: "text.disabled" }}>
                    {s.rejected > 0 ? fmtNum(s.rejected) : EMPTY}
                  </TableCell>
                  <TableCell align="right" sx={s.flagged > 0 ? { color: "#B45309", fontWeight: 700 } : { color: "text.disabled" }}>
                    {s.flagged > 0 ? fmtNum(s.flagged) : EMPTY}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>{fmtNum(s.total)}</TableCell>
                  <TableCell align="right">{fmtNum(s.unique_items)}</TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    {(() => {
                      const byPf = Object.fromEntries((s.by_platform ?? []).map((p) => [p.platform, p.total]));
                      return `Web ${fmtNum(byPf.web ?? 0)} · Mobile ${fmtNum(byPf.mobile ?? 0)}`;
                    })()}
                  </TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 0.25 }}>
                      {s.user?.id && (
                        <RowActions
                          visible={2}
                          actions={[
                            { label: "See their items in By Item", icon: <VisibilityIcon fontSize="small" />, onClick: () => onShowItems?.(s.user.id) },
                            { label: "Open their questions in Question Bank", icon: <FilterListIcon fontSize="small" />, onClick: () => handleViewQuestions(s.user.id) },
                          ]}
                        />
                      )}
                      <Tooltip title={isOpen ? "Hide breakdown" : "Breakdown by type"}>
                        <IconButton size="small" onClick={() => setExpanded(isOpen ? null : key)} aria-label={isOpen ? "Hide breakdown" : "Breakdown by type"}>
                          {isOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell colSpan={9} sx={{ py: 0, borderBottom: isOpen ? undefined : "none" }}>
                    <Collapse in={isOpen} unmountOnExit>
                      <Box sx={{ p: 1.5, my: 0.5, bgcolor: "#FAFAFF", borderRadius: 1 }}>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary", display: "block", mb: 1 }}>
                          Breakdown by type
                        </Typography>
                        {s.by_type?.length > 0 ? (
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 700, py: 0.5 }}>Type</TableCell>
                                <TableCell sx={{ fontWeight: 700, py: 0.5 }}>Approved</TableCell>
                                <TableCell sx={{ fontWeight: 700, py: 0.5 }}>Rejected</TableCell>
                                <TableCell sx={{ fontWeight: 700, py: 0.5 }}>Flagged</TableCell>
                                <TableCell sx={{ fontWeight: 700, py: 0.5 }}>Total</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {s.by_type.map((t) => (
                                <TableRow key={t.type}>
                                  <TableCell sx={{ py: 0.75 }}><TypeChip type={t.type} /></TableCell>
                                  <TableCell sx={{ py: 0.75 }}>{t.approved > 0 ? <Chip icon={<CheckCircleOutlineIcon />} label={t.approved} color="success" size="small" variant="outlined" /> : "—"}</TableCell>
                                  <TableCell sx={{ py: 0.75 }}>{t.rejected > 0 ? <Chip icon={<CancelOutlinedIcon />} label={t.rejected} color="error" size="small" variant="outlined" /> : "—"}</TableCell>
                                  <TableCell sx={{ py: 0.75 }}>{t.flagged > 0 ? <Chip label={t.flagged} color="warning" size="small" variant="outlined" /> : "—"}</TableCell>
                                  <TableCell sx={{ py: 0.75 }}><Chip label={t.total} size="small" variant="outlined" /></TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        ) : (
                          <Typography variant="body2" color="text.secondary">No breakdown available.</Typography>
                        )}
                        {s.by_platform?.length > 0 && (
                          <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: "wrap", alignItems: "center" }}>
                            <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary" }}>By platform:</Typography>
                            {s.by_platform.map((p) => (
                              <Chip key={p.platform} label={`${p.platform}: ${p.total}`} size="small" variant="outlined" color={PLATFORM_COLORS[p.platform] || "default"} />
                            ))}
                          </Stack>
                        )}
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// ── Main dashboard ────────────────────────────────────────────────────────────

const localDateIso = (date = new Date()) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const daysAgoIso = (days) => {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return localDateIso(d);
};

const todayIso = () => localDateIso();
const PAGE_SIZE = 25;

function DashboardPagination({ page, totalPages, onChange, total, noun = "records", pageSize = PAGE_SIZE, compact = false, disabled = false }) {
  const [jump, setJump] = useState("");
  if (!total) return null;
  const pages = Math.max(1, totalPages || Math.ceil(total / pageSize));
  const current = Math.min(Math.max(1, page), pages);
  const first = (current - 1) * pageSize + 1;
  const last = Math.min(current * pageSize, total);
  const go = (pg) => {
    const target = Math.min(Math.max(1, pg), pages);
    if (target !== current) onChange?.(null, target);
  };
  const submitJump = (e) => {
    e.preventDefault();
    const n = parseInt(jump, 10);
    if (Number.isFinite(n)) go(n);
    setJump("");
  };

  const range = (
    <Typography variant="body2" color="text.secondary">
      Showing <b>{fmtNum(first)}–{fmtNum(last)}</b> of <b>{fmtNum(total)}</b> {noun}
      {pages > 1 && <> · page {fmtNum(current)} of {fmtNum(pages)}</>}
    </Typography>
  );

  const prevNext = (label, target, isDisabled) => (
    <Button
      size="small"
      variant="outlined"
      disabled={disabled || isDisabled}
      onClick={() => go(target)}
      sx={{ textTransform: "none", fontWeight: 600, minWidth: 0, px: 1.5 }}
    >
      {label}
    </Button>
  );

  if (compact) {
    return (
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1, flexWrap: "wrap", gap: 1 }}>
        {range}
        {pages > 1 && (
          <Stack direction="row" spacing={1}>
            {prevNext("‹ Previous", current - 1, current <= 1)}
            {prevNext("Next ›", current + 1, current >= pages)}
          </Stack>
        )}
      </Box>
    );
  }

  return (
    <Paper variant="outlined" sx={{ mt: 2, px: 2, py: 1.25, borderRadius: 2, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1.5 }}>
      {range}
      {pages > 1 && (
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
          {prevNext("‹ Previous", current - 1, current <= 1)}
          <Pagination
            count={pages}
            page={current}
            onChange={(_, pg) => go(pg)}
            color="primary"
            shape="rounded"
            siblingCount={1}
            boundaryCount={1}
            hidePrevButton
            hideNextButton
            disabled={disabled}
          />
          {prevNext("Next ›", current + 1, current >= pages)}
          {pages > 7 && (
            <Box component="form" onSubmit={submitJump} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <TextField
                size="small"
                type="number"
                label="Go to page"
                value={jump}
                onChange={(e) => setJump(e.target.value)}
                inputProps={{ min: 1, max: pages }}
                sx={{ width: 110 }}
              />
              <Button type="submit" size="small" variant="contained" disableElevation disabled={disabled || !jump} sx={{ textTransform: "none", fontWeight: 600 }}>
                Go
              </Button>
            </Box>
          )}
        </Stack>
      )}
    </Paper>
  );
}
function groupByTester(rows) {
  const map = {};
  rows.forEach((r) => {
    const name = r.user?.name ?? `User #${r.user_id}`;
    if (!map[name]) map[name] = { name, approved: 0, rejected: 0, flagged: 0, web: 0, mobile: 0, last: null, items: [] };
    const g = map[name];
    if (r.status === "approved") g.approved += 1;
    else if (r.status === "rejected") g.rejected += 1;
    else if (r.status === "flagged") g.flagged += 1;
    if (r.platform === "mobile") g.mobile += 1;
    else if (r.platform === "web") g.web += 1;
    if (r.verified_at && (!g.last || new Date(r.verified_at) > new Date(g.last))) g.last = r.verified_at;
    g.items.push(r);
  });
  return Object.values(map).sort((a, b) => b.items.length - a.items.length);
}

function TesterActivityPanel({ rows, loading, error, onRetry }) {
  const [expandedTester, setExpandedTester] = useState(null);

  if (loading && !(rows?.length)) return <LoadingBlock />;
  if (error) return <LoadError what="tester activity" error={error} onRetry={onRetry} />;

  const groups = groupByTester(rows);
  if (groups.length === 0) return <EmptyBlock>No verifications recorded for these dates.</EmptyBlock>;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, opacity: loading ? 0.55 : 1, transition: "opacity .15s" }}>
      {loading && <LinearProgress />}
      {groups.map((g) => {
        const isOpen = expandedTester === g.name;
        return (
          <Paper key={g.name} variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
            <Box
              component="button"
              type="button"
              aria-expanded={isOpen}
              onClick={() => setExpandedTester(isOpen ? null : g.name)}
              sx={{
                width: "100%", border: 0, font: "inherit", textAlign: "left", color: "inherit",
                px: 2, py: 1.5, cursor: "pointer", bgcolor: isOpen ? "#FAFAFF" : "background.paper", "&:hover": { bgcolor: "#FAFAFF" },
                display: "grid", alignItems: "center", gap: 2,
                gridTemplateColumns: { xs: "1fr auto", md: "220px minmax(120px, 1fr) auto auto auto" },
              }}
            >
              <Box>
                <Typography sx={{ fontWeight: 700 }}>{g.name}</Typography>
                <Typography variant="caption" color="text.secondary">Last verdict {formatDateTime(g.last)}</Typography>
              </Box>
              <Box sx={{ display: { xs: "none", md: "flex" }, height: 10, borderRadius: 5, overflow: "hidden", bgcolor: "action.hover" }}>
                <Box sx={{ width: `${(g.web / (g.items.length || 1)) * 100}%`, bgcolor: "#0369A1" }} />
                <Box sx={{ width: `${(g.mobile / (g.items.length || 1)) * 100}%`, bgcolor: "#F59E0B" }} />
              </Box>
              <Typography variant="body2" sx={{ display: { xs: "none", md: "block" }, whiteSpace: "nowrap" }}>
                Web <b>{fmtNum(g.web)}</b> · Mobile <b>{fmtNum(g.mobile)}</b>
              </Typography>
              <Stack direction="row" spacing={0.75} sx={{ display: { xs: "none", md: "flex" } }}>
                {g.approved > 0 && <StatusChip tone="green" label={`${fmtNum(g.approved)} approved`} />}
                {g.rejected > 0 && <StatusChip tone="red" label={`${fmtNum(g.rejected)} rejected`} />}
                {g.flagged > 0 && <StatusChip tone="amber" label={`${fmtNum(g.flagged)} flagged`} />}
              </Stack>
              {isOpen ? <ExpandLessIcon fontSize="small" color="action" /> : <ExpandMoreIcon fontSize="small" color="action" />}
            </Box>
            <Collapse in={isOpen} unmountOnExit>
              <Box sx={{ px: 0 }}>
                <Box sx={{ overflowX: "auto" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Type</TableCell>
                      <TableCell>Item</TableCell>
                      <TableCell>Verdict</TableCell>
                      <TableCell>Platform</TableCell>
                      <TableCell>Notes</TableCell>
                      <TableCell>When</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {g.items.map((r) => (
                        <TableRow key={r.id} hover>
                          <TableCell><TypeChip type={r.verifiable_type} /></TableCell>
                          <TableCell sx={{ fontFamily: "monospace", fontWeight: 600 }}>#{r.verifiable_id}</TableCell>
                          <TableCell><VerdictChip verdict={r.status || "unknown"} /></TableCell>
                          <TableCell><PlatformChip platform={r.platform} /></TableCell>
                          <TableCell sx={{ maxWidth: 300 }}>
                            {r.notes
                              ? <Tooltip title={r.notes}><Typography variant="body2" sx={clampSx(2)}>{r.notes}</Typography></Tooltip>
                              : <Typography variant="caption" color="text.disabled">{EMPTY}</Typography>}
                          </TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>{formatDateTime(r.verified_at)}</TableCell>
                        </TableRow>
                    ))}
                  </TableBody>
                </Table>
                </Box>
              </Box>
            </Collapse>
          </Paper>
        );
      })}
    </Box>
  );
}

export default function VerificationDashboard() {
  const navigate = useNavigate();
  // The open tab lives in the address (?tab=papers) so refresh and shared links land on it.
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const tab = TAB_VALUES.has(tabParam) ? tabParam : "items";
  const setTab = useCallback((value) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("tab", value);
      return next;
    }, { replace: true });
  }, [setSearchParams]);
  const isWorksheetTab = tab === "worksheets";
  const isMockExamTab = tab === "mock-exams";
  const isCoverageTab = tab === "papers" || isWorksheetTab || isMockExamTab;
  const coverageEntity = isWorksheetTab ? "worksheet" : isMockExamTab ? "mock exam" : "paper";
  const coverageEntityTitle = coverageEntity.replace(/\b\w/g, (c) => c.toUpperCase());

  // ── Records view state
  const [rows, setRows] = useState([]);
  const [rowsLoading, setRowsLoading] = useState(false);
  const [rowsError, setRowsError] = useState(null);
  const [rowsPage, setRowsPage] = useState(1);
  const [rowsTotalPages, setRowsTotalPages] = useState(1);
  const [rowsTotal, setRowsTotal] = useState(0);

  // ── Items (grouped) view state
  const [items, setItems] = useState([]);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [itemsError, setItemsError] = useState(null);
  const [itemsPage, setItemsPage] = useState(1);
  const [itemsTotalPages, setItemsTotalPages] = useState(1);
  const [itemsTotal, setItemsTotal] = useState(0);
  const [itemsSummary, setItemsSummary] = useState({ total: 0, both_verified: 0, web_only: 0, mobile_only: 0, unverified: 0 });

  // ── Tester activity (today)
  const [activityRows, setActivityRows] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityError, setActivityError] = useState(null);
  const [activityPage, setActivityPage] = useState(1);
  const [activityTotalPages, setActivityTotalPages] = useState(1);
  const [activityTotal, setActivityTotal] = useState(0);

  // ── Papers (paper-wise coverage) view state
  const [papers, setPapers] = useState([]);
  const [papersTab, setPapersTab] = useState(null);
  const [papersLoading, setPapersLoading] = useState(false);
  const [papersError, setPapersError] = useState(null);
  const [papersPage, setPapersPage] = useState(1);
  const [papersTotalPages, setPapersTotalPages] = useState(1);
  const [papersTotal, setPapersTotal] = useState(0);
  const [papersSummary, setPapersSummary] = useState({ total: 0, fully_verified: 0, partially_verified: 0, not_started: 0, paper_approved: 0 });
  const [filterPaperCoverage, setFilterPaperCoverage] = useState("all");
  const [filterPaperSubjectId, setFilterPaperSubjectId] = useState("");
  const [filterPaperShelf, setFilterPaperShelf] = useState("");
  const [filterChecker, setFilterChecker] = useState("");
  const [subjectOptions, setSubjectOptions] = useState([]);
  const [openingQuestionId, setOpeningQuestionId] = useState(null);
  const [filterPaperSearch, setFilterPaperSearch] = useState("");
  const [filterExamCategoryId, setFilterExamCategoryId] = useState("");
  const [filterDifficulty, setFilterDifficulty] = useState("");
  const [examOptions, setExamOptions] = useState([]);
  const [examCategoryOptions, setExamCategoryOptions] = useState([]);

  // ── Tester stats (all-time)
  const [testerStats, setTesterStats] = useState([]);
  const [testerStatsLoading, setTesterStatsLoading] = useState(false);
  const [testerStatsError, setTesterStatsError] = useState(null);
  const [statsPage, setStatsPage] = useState(1);
  const [statsTotalPages, setStatsTotalPages] = useState(1);
  const [statsTotal, setStatsTotal] = useState(0);

  // ── Question view / edit dialogs
  const [viewQDialogOpen, setViewQDialogOpen] = useState(false);
  const [viewQData, setViewQData] = useState(null);
  const [viewQLoading, setViewQLoading] = useState(false);
  const [viewQVerifications, setViewQVerifications] = useState([]);
  const [viewQVerificationsLoading, setViewQVerificationsLoading] = useState(false);
  const [editQOpen, setEditQOpen] = useState(false);
  const [editQData, setEditQData] = useState(null);
  const [editQSaving, setEditQSaving] = useState(false);

  // ── Verify (accept/reject) dialog
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [verifyItem, setVerifyItem] = useState(null);
  const [verifyQuestion, setVerifyQuestion] = useState(null);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [verifySubmitting, setVerifySubmitting] = useState(false);

  // Tester filter options
  const [testerOptions, setTesterOptions] = useState([]);
  const [filterTesterUserId, setFilterTesterUserId] = useState("");
  const [verifierSummary, setVerifierSummary] = useState(null);
  const [verifierSummaryLoading, setVerifierSummaryLoading] = useState(false);
  const [verifierSummaryError, setVerifierSummaryError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get("common/data?param=Subjects")
      .then((res) => setSubjectOptions(res.data?.data ?? []))
      .catch(() => {});
    api.get("admin/content-verifications/verifiers").then((res) => {
      if (!cancelled) setTesterOptions(Array.isArray(res.data?.data) ? res.data.data : []);
    }).catch(() => {});
    // The tree endpoint is the only one that reaches child categories; /categories is top-level only.
    api.get("admin/mock-exam/category-tree")
      .then((res) => { if (!cancelled) setExamCategoryOptions(flattenExamCategories(res.data?.data ?? [])); })
      .catch(() => {
        api.get("admin/mock-exam/categories")
          .then((res) => { if (!cancelled) setExamCategoryOptions(res.data?.data ?? []); })
          .catch(() => {});
      });
    return () => { cancelled = true; };
  }, []);

  // Mock exam names for the By Mock Exam autocomplete — debounced as the admin types.
  const examSearchTimer = useRef(null);
  useEffect(() => {
    if (!isMockExamTab) return undefined;
    clearTimeout(examSearchTimer.current);
    examSearchTimer.current = setTimeout(async () => {
      try {
        const params = { per_page: 50, order_by: "name", order_dir: "asc" };
        if (filterPaperSearch) params.search = filterPaperSearch;
        if (filterPaperSubjectId) params.subject_id = filterPaperSubjectId;
        if (filterExamCategoryId) params.category_id = filterExamCategoryId;
        const res = await api.get("/admin/mock-exam", { params });
        const items = res.data?.data?.data ?? res.data?.data ?? [];
        setExamOptions(items.map((e) => ({ id: e.id, name: e.name })));
      } catch {
        // silently ignore — the dropdown just stays empty and free text still filters
      }
    }, 300);
    return () => clearTimeout(examSearchTimer.current);
  }, [isMockExamTab, filterPaperSearch, filterPaperSubjectId, filterExamCategoryId]);

  // Shared filters
  const [filterType, setFilterType] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterPlatform, setFilterPlatform] = useState("");
  const [filterCoverage, setFilterCoverage] = useState("");
  const [filterVerdict, setFilterVerdict] = useState("");
  const [filterDateFrom, setFilterDateFrom] = useState(() => daysAgoIso(30));
  const [filterDateTo, setFilterDateTo] = useState(() => localDateIso());
  const [minVerifiers, setMinVerifiers] = useState("");

  // ── Tester edits (question.updated history)
  const [edits, setEdits] = useState([]);
  const [editsLoading, setEditsLoading] = useState(false);
  const [editsError, setEditsError] = useState(null);
  const [editsPage, setEditsPage] = useState(1);
  const [editsTotalPages, setEditsTotalPages] = useState(1);
  const [editsTotal, setEditsTotal] = useState(0);
  const [filterQuestionId, setFilterQuestionId] = useState("");

  // Only the newest request of each list may write its result, so quick page clicks never
  // leave an older page's rows under a newer page number.
  const reqSeq = useRef({});
  const nextSeq = (key) => {
    reqSeq.current[key] = (reqSeq.current[key] || 0) + 1;
    return reqSeq.current[key];
  };
  const isLatest = (key, seq) => reqSeq.current[key] === seq;

  const resetAllPages = useCallback(() => {
    setRowsPage(1);
    setItemsPage(1);
    setActivityPage(1);
    setEditsPage(1);
    setStatsPage(1);
    setPapersPage(1);
  }, []);

  const [hideFormattingEdits, setHideFormattingEdits] = useState(true);
  const listTopRef = useRef(null);

  const fetchRows = useCallback(async (pg = 1) => {
    const seq = nextSeq("rows");
    setRowsLoading(true);
    setRowsError(null);
    try {
      const params = { page: pg, per_page: PAGE_SIZE };
      if (filterType) params.type = filterType;
      if (filterStatus) params.status = filterStatus;
      if (filterPlatform) params.platform = filterPlatform;
      if (filterDateFrom) params.date_from = filterDateFrom;
      if (filterDateTo) params.date_to = filterDateTo;
      if (minVerifiers) params.min_verifiers = minVerifiers;
      if (filterTesterUserId) params.user_id = filterTesterUserId;
      const res = await api.get("admin/content-verifications", { params });
      if (!isLatest("rows", seq)) return;
      if (res.data?.success) {
        const d = res.data.data;
        setRows(d?.verifications ?? d?.data ?? []);
        setRowsTotalPages(d?.pagination?.last_page ?? d?.last_page ?? 1);
        setRowsTotal(d?.pagination?.total ?? d?.total ?? 0);
      }
    } catch (e) {
      if (!isLatest("rows", seq)) return;
      setRowsError(e || {});
    } finally {
      if (isLatest("rows", seq)) setRowsLoading(false);
    }
  }, [filterType, filterStatus, filterPlatform, filterDateFrom, filterDateTo, minVerifiers, filterTesterUserId]);

  const fetchItems = useCallback(async (pg = 1) => {
    const seq = nextSeq("items");
    setItemsLoading(true);
    setItemsError(null);
    try {
      const params = { page: pg, per_page: PAGE_SIZE };
      if (filterType) params.type = filterType;
      if (filterPlatform) params.platform = filterPlatform;
      if (minVerifiers) params.min_verifiers = minVerifiers;
      if (filterTesterUserId) params.user_id = filterTesterUserId;
      if (filterQuestionId) params.verifiable_id = filterQuestionId;
      if (filterDateFrom) params.date_from = filterDateFrom;
      if (filterDateTo) params.date_to = filterDateTo;
      if (filterCoverage) params.coverage = filterCoverage;
      if (filterVerdict) params.verdict = filterVerdict;
      const res = await api.get("admin/content-verifications/item-aggregates", { params });
      if (!isLatest("items", seq)) return;
      if (res.data?.success) {
        const d = res.data.data;
        setItems(d?.data ?? []);
        setItemsTotalPages(d?.last_page ?? 1);
        setItemsTotal(d?.total ?? 0);
        setItemsSummary(d?.summary ?? { total: 0, both_verified: 0, web_only: 0, mobile_only: 0, unverified: 0 });
      }
    } catch (e) {
      if (!isLatest("items", seq)) return;
      setItemsError(e || {});
    } finally {
      if (isLatest("items", seq)) setItemsLoading(false);
    }
  }, [filterType, filterPlatform, minVerifiers, filterTesterUserId, filterQuestionId, filterDateFrom, filterDateTo, filterCoverage, filterVerdict]);

  // Opens the paper/worksheet in the student take view (the only place a question can be verified)
  // parked on the unverified question. Worksheets take their own id; papers need a purchase row,
  // which /start mints for a verifier.
  const openQuestionForVerification = useCallback(async (entityId, question) => {
    setOpeningQuestionId(question.id);
    if (isWorksheetTab) {
      navigate(`/${STUDENT_BASE}/work-sheet/${entityId}/take`, { state: { jumpToQuestionId: question.id } });
      setOpeningQuestionId(null);
      return;
    }
    if (isMockExamTab) {
      navigate(`/${STUDENT_BASE}/mock-exam/${entityId}/take`, { state: { jumpToQuestionId: question.id } });
      setOpeningQuestionId(null);
      return;
    }
    try {
      let purchaseId = null;
      try {
        const res = await api.post(`/student/paper/${entityId}/start`);
        purchaseId = res.data?.data?.purchase_id ?? res.data?.purchase_id;
      } catch (startErr) {
        // "Already started" / "already completed" still leaves a usable purchase to resume.
        const res = await api.get("/student/paper/my-purchases");
        const list = res.data?.data?.data ?? res.data?.data ?? [];
        const match = (Array.isArray(list) ? list : []).find((p) => Number(p.paper_id) === Number(entityId));
        purchaseId = match?.id ?? match?.purchase_id ?? null;
        if (!purchaseId) throw startErr;
      }
      if (!purchaseId) {
        toast.error("Could not open this paper.");
        return;
      }
      navigate(`/${STUDENT_BASE}/past-paper/${purchaseId}/take`, {
        state: { jumpToQuestionId: question.id },
      });
    } catch (e) {
      toast.error(e.response?.data?.message || "Could not open this paper.");
    } finally {
      setOpeningQuestionId(null);
    }
  }, [navigate, isWorksheetTab, isMockExamTab]);

  const fetchPapers = useCallback(async (pg = 1) => {
    const seq = nextSeq("papers");
    setPapersLoading(true);
    setPapersError(null);
    try {
      const params = { page: pg, per_page: PAGE_SIZE };
      if (filterPlatform) params.platform = filterPlatform;
      if (filterTesterUserId) params.user_id = filterTesterUserId;
      if (filterDateFrom) params.date_from = filterDateFrom;
      if (filterDateTo) params.date_to = filterDateTo;
      if (filterPaperCoverage) params.coverage = filterPaperCoverage;
      if (filterPaperSubjectId) params.subject_id = filterPaperSubjectId;
      if (filterPaperSearch) params.search = filterPaperSearch;
      if (isMockExamTab && filterExamCategoryId) params.category_id = filterExamCategoryId;
      if (!isMockExamTab && !isWorksheetTab && filterPaperShelf) params.shelf = filterPaperShelf;
      if (isWorksheetTab && filterDifficulty) params.difficulty = filterDifficulty;
      if (!filterTesterUserId && filterChecker) params.checker = filterChecker;
      const endpoint = isWorksheetTab
        ? "admin/content-verifications/worksheet-aggregates"
        : isMockExamTab
          ? "admin/content-verifications/mock-exam-aggregates"
          : "admin/content-verifications/paper-aggregates";
      const res = await api.get(endpoint, { params });
      if (!isLatest("papers", seq)) return;
      if (res.data?.success) {
        const d = res.data.data;
        setPapersTab(tab);
        setPapers((d?.data ?? []).map((r) => ({
          ...r,
          paper_id: r.paper_id ?? r.worksheet_id ?? r.mock_exam_id,
          paper_name: r.paper_name ?? r.worksheet_name ?? r.mock_exam_name,
        })));
        setPapersTotalPages(d?.last_page ?? 1);
        setPapersTotal(d?.total ?? 0);
        setPapersSummary(d?.summary ?? { total: 0, fully_verified: 0, partially_verified: 0, not_started: 0, paper_approved: 0 });
      }
    } catch (e) {
      if (!isLatest("papers", seq)) return;
      // Clear the counts too — the three coverage tabs share this state, so a failed load would
      // otherwise leave the previous tab's totals sitting under the new tab's labels.
      setPapers([]);
      setPapersTotal(0);
      setPapersTotalPages(1);
      setPapersSummary({ total: 0, fully_verified: 0, partially_verified: 0, not_started: 0, paper_approved: 0 });
      setPapersError(e || {});
    } finally {
      if (isLatest("papers", seq)) setPapersLoading(false);
    }
  }, [tab, isWorksheetTab, isMockExamTab, filterPlatform, filterTesterUserId, filterDateFrom, filterDateTo, filterPaperCoverage, filterPaperSubjectId, filterPaperShelf, filterPaperSearch, filterExamCategoryId, filterDifficulty, filterChecker]);

  // "Verified by" summary across papers, mock exams and worksheets for the chosen verifier.
  useEffect(() => {
    if (!filterTesterUserId) {
      setVerifierSummary(null);
      setVerifierSummaryError(null);
      return undefined;
    }
    let cancelled = false;
    setVerifierSummaryLoading(true);
    setVerifierSummaryError(null);
    const params = { user_id: filterTesterUserId };
    if (filterPlatform) params.platform = filterPlatform;
    if (filterDateFrom) params.date_from = filterDateFrom;
    if (filterDateTo) params.date_to = filterDateTo;
    api.get("admin/content-verifications/verifier-summary", { params })
      .then((res) => { if (!cancelled) setVerifierSummary(res.data?.success ? res.data.data : null); })
      .catch(() => { if (!cancelled) { setVerifierSummary(null); setVerifierSummaryError("Could not load the Verified by summary."); } })
      .finally(() => { if (!cancelled) setVerifierSummaryLoading(false); });
    return () => { cancelled = true; };
  }, [filterTesterUserId, filterPlatform, filterDateFrom, filterDateTo]);

  // The verifier-only coverage choices make no sense once the verifier is cleared.
  useEffect(() => {
    if (!filterTesterUserId && String(filterPaperCoverage).startsWith("verifier_")) {
      setFilterPaperCoverage("all");
    }
  }, [filterTesterUserId, filterPaperCoverage]);

  const selectedVerifierName = filterTesterUserId
    ? (testerOptions.find((o) => String(o.id) === String(filterTesterUserId))?.name ?? verifierSummary?.verifier?.name ?? `User #${filterTesterUserId}`)
    : null;

  const fetchActivity = useCallback(async (pg = 1) => {
    const seq = nextSeq("activity");
    setActivityLoading(true);
    setActivityError(null);
    try {
      const params = { date_from: filterDateFrom || todayIso(), date_to: filterDateTo || todayIso(), page: pg, per_page: PAGE_SIZE };
      if (filterType) params.type = filterType;
      if (filterPlatform) params.platform = filterPlatform;
      if (filterTesterUserId) params.user_id = filterTesterUserId;
      const res = await api.get("admin/content-verifications", { params });
      if (!isLatest("activity", seq)) return;
      if (res.data?.success) {
        const d = res.data.data;
        setActivityRows(d?.verifications ?? d?.data ?? []);
        setActivityTotalPages(d?.pagination?.last_page ?? d?.last_page ?? 1);
        setActivityTotal(d?.pagination?.total ?? d?.total ?? 0);
      }
    } catch (e) {
      if (!isLatest("activity", seq)) return;
      setActivityError(e || {});
    } finally {
      if (isLatest("activity", seq)) setActivityLoading(false);
    }
  }, [filterType, filterPlatform, filterTesterUserId, filterDateFrom, filterDateTo]);

  const fetchTesterStats = useCallback(async (pg = 1) => {
    const seq = nextSeq("stats");
    setTesterStatsLoading(true);
    setTesterStatsError(null);
    try {
      const params = { page: pg, per_page: PAGE_SIZE };
      if (filterType) params.type = filterType;
      if (filterPlatform) params.platform = filterPlatform;
      if (filterDateFrom) params.date_from = filterDateFrom;
      if (filterDateTo) params.date_to = filterDateTo;
      const res = await api.get("admin/content-verifications/tester-stats", { params });
      if (!isLatest("stats", seq)) return;
      if (res.data?.success) {
        const d = res.data.data;
        setTesterStats(d?.data ?? (Array.isArray(d) ? d : []));
        setStatsTotalPages(d?.last_page ?? 1);
        setStatsTotal(d?.total ?? 0);
      }
    } catch (e) {
      if (!isLatest("stats", seq)) return;
      setTesterStatsError(e || {});
    } finally {
      if (isLatest("stats", seq)) setTesterStatsLoading(false);
    }
  }, [filterType, filterPlatform, filterDateFrom, filterDateTo]);

  const fetchEdits = useCallback(async (pg = 1) => {
    const seq = nextSeq("edits");
    setEditsLoading(true);
    setEditsError(null);
    try {
      const params = { page: pg, per_page: PAGE_SIZE };
      if (filterPlatform) params.platform = filterPlatform;
      if (filterDateFrom) params.date_from = filterDateFrom;
      if (filterDateTo) params.date_to = filterDateTo;
      if (filterTesterUserId) params.user_id = filterTesterUserId;
      if (filterQuestionId) params.verifiable_id = filterQuestionId;
      const res = await api.get("admin/content-verifications/edits", { params });
      if (!isLatest("edits", seq)) return;
      if (res.data?.success) {
        const d = res.data.data;
        setEdits(d?.data ?? []);
        setEditsTotalPages(d?.last_page ?? 1);
        setEditsTotal(d?.total ?? 0);
      }
    } catch (e) {
      if (!isLatest("edits", seq)) return;
      setEditsError(e || {});
    } finally {
      if (isLatest("edits", seq)) setEditsLoading(false);
    }
  }, [filterPlatform, filterDateFrom, filterDateTo, filterTesterUserId, filterQuestionId]);

  const handleLoadQuestion = useCallback(async (id, mode) => {
    if (mode === "view") {
      setViewQLoading(true);
      setViewQDialogOpen(true);
      setViewQData(null);
      setViewQVerifications([]);
      setViewQVerificationsLoading(true);
    }
    try {
      const [qRes, vRes] = await Promise.all([
        api.get(`/admin/question-bank/${id}`),
        mode === "view"
          ? api.get("admin/content-verifications", { params: { type: "question", verifiable_id: id, per_page: 50 } }).catch(() => null)
          : Promise.resolve(null),
      ]);
      const q = qRes.data?.data;
      if (mode === "view") {
        setViewQData(q);
        const d = vRes?.data?.data;
        setViewQVerifications(d?.verifications ?? d?.data ?? []);
      } else {
        setEditQData(q);
        setEditQOpen(true);
      }
    } catch {
      toast.error("Failed to load question");
      if (mode === "view") setViewQDialogOpen(false);
    } finally {
      if (mode === "view") {
        setViewQLoading(false);
        setViewQVerificationsLoading(false);
      }
    }
  }, []);

  const handleOpenVerify = useCallback(async (item) => {
    setVerifyItem(item);
    setVerifyQuestion(null);
    setVerifyOpen(true);
    setVerifyLoading(true);
    try {
      const res = await api.get(`/admin/question-bank/${item.verifiable_id}`);
      const q = res.data?.data;
      setVerifyQuestion(q ? { ...q, paper_number: q.paper_number ?? q.id } : null);
    } catch {
      toast.error("Failed to load question");
    } finally {
      setVerifyLoading(false);
    }
  }, []);

  const handleSubmitVerify = useCallback(async ({ status, notes, platforms }) => {
    if (!verifyItem) return;
    setVerifySubmitting(true);
    try {
      await api.post("admin/content-verifications", {
        verifiable_type: verifyItem.verifiable_type,
        verifiable_id: verifyItem.verifiable_id,
        status,
        notes,
        platforms,
      });
      toast.success(status === "approved" ? "Marked as approved." : "Marked as rejected.");
      setVerifyOpen(false);
      setVerifyItem(null);
      setVerifyQuestion(null);
      fetchItems(itemsPage);
    } catch {
      toast.error("Failed to save verdict.");
    } finally {
      setVerifySubmitting(false);
    }
  }, [verifyItem, fetchItems, itemsPage]);

  const verifyItemData = (() => {
    if (!verifyItem) return null;
    const uid = getCurrentUserId();
    const myVerdicts = {};
    (verifyItem.verifications || []).forEach((v) => {
      if (v.user?.id === uid && v.platform) myVerdicts[v.platform] = v.status;
    });
    return {
      my_verdicts: myVerdicts,
      aggregate: {
        approved: verifyItem.approved ?? 0,
        rejected: verifyItem.rejected ?? 0,
        flagged: verifyItem.flagged ?? 0,
        total: verifyItem.total ?? 0,
      },
    };
  })();

  useEffect(() => {
    if (tab === "records") { fetchRows(rowsPage); }
    else if (isCoverageTab) { fetchPapers(papersPage); }
    else if (tab === "activity") { fetchActivity(activityPage); }
    else if (tab === "tester-stats") { fetchTesterStats(statsPage); }
    else if (tab === "edits") { fetchEdits(editsPage); }
    else if (tab === "videos") { /* the Videos panel loads its own data */ }
    else { fetchItems(itemsPage); }
  }, [
    tab, rowsPage, itemsPage, papersPage, activityPage, editsPage, statsPage,
    isCoverageTab,
    filterType, filterStatus, filterPlatform, filterDateFrom, filterDateTo, minVerifiers, filterTesterUserId, filterQuestionId, filterCoverage, filterVerdict, filterPaperCoverage, filterPaperSubjectId, filterPaperSearch, filterExamCategoryId, filterDifficulty, filterChecker,
    fetchRows, fetchItems, fetchPapers, fetchActivity, fetchTesterStats, fetchEdits,
  ]);

  const refreshCurrentTab = () => {
    if (tab === "records") fetchRows(rowsPage);
    else if (isCoverageTab) fetchPapers(papersPage);
    else if (tab === "activity") fetchActivity(activityPage);
    else if (tab === "tester-stats") fetchTesterStats(statsPage);
    else if (tab === "edits") fetchEdits(editsPage);
    else if (tab === "items") fetchItems(itemsPage);
  };

  const refreshing = rowsLoading || itemsLoading || papersLoading || activityLoading || testerStatsLoading || editsLoading;

  const resetFilters = () => {
    resetAllPages();
    setFilterType("");
    setFilterStatus("");
    setFilterPlatform("");
    setFilterCoverage("");
    setFilterVerdict("");
    setMinVerifiers("");
    setFilterTesterUserId("");
    setFilterQuestionId("");
    setFilterDateFrom(daysAgoIso(30));
    setFilterDateTo(localDateIso());
    setFilterPaperCoverage("all");
    setFilterPaperSubjectId("");
    setFilterPaperShelf("");
    setFilterChecker("");
    setFilterPaperSearch("");
    setFilterExamCategoryId("");
    setFilterDifficulty("");
  };

  const setDates = (from, to) => {
    resetAllPages();
    setFilterDateFrom(from);
    setFilterDateTo(to);
  };

  const dateRange = <DateRange from={filterDateFrom} to={filterDateTo} onChange={setDates} />;

  const questionIdFilter = (
    <TextField
      size="small"
      label="Question ID"
      type="number"
      placeholder="e.g. 18234"
      value={filterQuestionId}
      onChange={(e) => { resetAllPages(); setFilterQuestionId(e.target.value); }}
      sx={{ width: 140 }}
      inputProps={{ min: 1 }}
    />
  );

  const typeFilter = (
    <FormControl size="small" sx={{ minWidth: 140 }}>
      <InputLabel>Type</InputLabel>
      <Select value={filterType} label="Type" onChange={(e) => { resetAllPages(); setFilterType(e.target.value); }}>
        <MenuItem value="">All</MenuItem>
        <MenuItem value="question">Question</MenuItem>
        <MenuItem value="worksheet">Worksheet</MenuItem>
        <MenuItem value="paper">Paper</MenuItem>
        <MenuItem value="mock_exam">Mock Exam</MenuItem>
      </Select>
    </FormControl>
  );

  const testerFilter = (
    <Autocomplete
      options={testerOptions}
      getOptionLabel={(o) => (typeof o === "string" ? o : o.name ?? "")}
      isOptionEqualToValue={(o, v) => String(o.id) === String(v.id)}
      value={testerOptions.find((o) => String(o.id) === String(filterTesterUserId)) ?? null}
      onChange={(_, selected) => {
        resetAllPages();
        setFilterChecker("");
        setFilterTesterUserId(selected ? String(selected.id) : "");
      }}
      size="small"
      clearOnEscape
      sx={{ minWidth: 200 }}
      renderOption={(props, option) => (
        <li {...props} key={option.id}>
          <span style={{ flex: 1 }}>
            {option.name ?? option}
            {Array.isArray(option.linked_emails) && option.linked_emails.length > 1 && (
              <span style={{ display: "block", fontSize: "0.72rem", color: "#666", marginTop: 2 }}>
                {option.linked_emails.join(" · ")}
              </span>
            )}
          </span>
          {option.total_verifications > 0 && (
            <Chip label={option.total_verifications} size="small" variant="outlined" color="primary" sx={{ ml: 1, fontSize: "0.68rem", height: 20 }} />
          )}
        </li>
      )}
      renderInput={(params) => (
        <TextField {...params} label="Verified by" placeholder="Anyone" />
      )}
    />
  );

  const platformFilter = (
    <FormControl size="small" sx={{ minWidth: 130 }}>
      <InputLabel>Platform</InputLabel>
      <Select value={filterPlatform} label="Platform" onChange={(e) => { resetAllPages(); setFilterPlatform(e.target.value); }}>
        <MenuItem value="">All</MenuItem>
        <MenuItem value="web">Web</MenuItem>
        <MenuItem value="mobile">Mobile</MenuItem>
      </Select>
    </FormControl>
  );

  const coverageFilter = (
    <FormControl size="small" sx={{ minWidth: 180 }}>
      <InputLabel>Platform coverage</InputLabel>
      <Select value={filterCoverage} label="Platform coverage" onChange={(e) => { resetAllPages(); setFilterCoverage(e.target.value); }}>
        <MenuItem value="">All</MenuItem>
        <MenuItem value="both">Both verified</MenuItem>
        <MenuItem value="web_only">Web only</MenuItem>
        <MenuItem value="mobile_only">Mobile only</MenuItem>
        <MenuItem value="none">Unverified</MenuItem>
        <MenuItem value="has_rejected">Has rejected</MenuItem>
      </Select>
    </FormControl>
  );

  const verdictFilter = (
    <FormControl size="small" sx={{ minWidth: 150 }}>
      <InputLabel>Verdict</InputLabel>
      <Select value={filterVerdict} label="Verdict" onChange={(e) => { resetAllPages(); setFilterVerdict(e.target.value); }}>
        <MenuItem value="">All</MenuItem>
        <MenuItem value="approved">Accepted</MenuItem>
        <MenuItem value="rejected">Rejected</MenuItem>
        <MenuItem value="mixed">Mixed</MenuItem>
      </Select>
    </FormControl>
  );

  const coverageSummary = {
    total: itemsSummary.total,
    bothVerified: itemsSummary.both_verified,
    webOnly: itemsSummary.web_only,
    mobileOnly: itemsSummary.mobile_only,
    unverified: itemsSummary.unverified,
  };

  const minVerifiersFilter = (
    <FormControl size="small" sx={{ minWidth: 160 }}>
      <InputLabel>Min. verifiers</InputLabel>
      <Select value={minVerifiers} label="Min. verifiers" onChange={(e) => { resetAllPages(); setMinVerifiers(e.target.value); }}>
        <MenuItem value="">Any count</MenuItem>
        <MenuItem value="1">1+ verified</MenuItem>
        <MenuItem value="2">2+ verified</MenuItem>
        <MenuItem value="3">3+ verified</MenuItem>
        <MenuItem value="4">4+ verified</MenuItem>
        <MenuItem value="5">5+ verified</MenuItem>
      </Select>
    </FormControl>
  );

  const activeTabMeta = ALL_TABS.find((t) => t.value === tab) ?? ALL_TABS[0];

  // Coverage rows from another coverage tab must never show under this tab's labels.
  const coverageRows = papersTab === tab ? papers : [];

  const goToPage = (setter) => (_, pg) => {
    setter(pg);
    listTopRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const coverageTiles = [
    { key: "all", label: `${coverageEntityTitle}s`, value: fmtNum(papersSummary.total), color: "default" },
    { key: "full", label: "Fully verified", value: fmtNum(papersSummary.fully_verified), color: "success" },
    { key: "partial", label: "Partial", value: fmtNum(papersSummary.partially_verified), color: "warning" },
    { key: "not_started", label: "Not started", value: fmtNum(papersSummary.not_started ?? 0), color: "default" },
    { key: "approved", label: `${coverageEntityTitle} sign-off`, value: fmtNum(papersSummary.paper_approved ?? 0), color: "success" },
    ...(papersSummary.verifier ? [
      { key: "verifier_all", label: `All verified by ${papersSummary.verifier.user?.name}`, value: fmtNum(papersSummary.verifier.items_all_verified), color: "primary" },
      { key: "verifier_any", label: `Some by ${papersSummary.verifier.user?.name}`, value: fmtNum(papersSummary.verifier.items_with_verifier), color: "primary" },
      { key: "verifier_none", label: `None by ${papersSummary.verifier.user?.name}`, value: fmtNum(papersSummary.verifier.items_none_verified), color: "default" },
    ] : []),
  ];

  const itemTiles = [
    { key: "", label: "All items", value: fmtNum(coverageSummary.total), color: "default" },
    { key: "both", label: "Both verified", value: fmtNum(coverageSummary.bothVerified), color: "success" },
    { key: "web_only", label: "Web only", value: fmtNum(coverageSummary.webOnly), color: "info" },
    { key: "mobile_only", label: "Mobile only", value: fmtNum(coverageSummary.mobileOnly), color: "secondary" },
    { key: "none", label: "Unverified", value: fmtNum(coverageSummary.unverified), color: "default" },
  ];

  const tabSx = { textTransform: "none", fontSize: 14, minHeight: 48, px: 1.75 };
  const groupLabelSx = {
    minWidth: 0, minHeight: 48, px: 1, fontSize: 11, fontWeight: 700, letterSpacing: ".06em",
    color: "text.secondary", opacity: "1 !important", cursor: "default",
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, minWidth: 0 }}>
      <Box sx={{ mb: 1.5 }}>
        <Typography variant="h5" sx={{ fontWeight: 800 }}>Verification Dashboard</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{activeTabMeta.hint}</Typography>
      </Box>

      <Tabs
        value={tab}
        onChange={(_, v) => { resetAllPages(); setTab(v); }}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{ mb: 2, borderBottom: "1px solid", borderColor: "divider" }}
      >
        {TAB_GROUPS.flatMap((group, gi) => [
          <Tab
            key={`group-${group.label}`}
            value={`group-${group.label}`}
            label={group.label.toUpperCase()}
            disabled
            sx={{ ...groupLabelSx, ...(gi > 0 ? { ml: 1.5, borderLeft: "1px solid", borderColor: "divider", pl: 2 } : {}) }}
          />,
          ...group.tabs.map((t) => <Tab key={t.value} value={t.value} label={t.label} sx={tabSx} />),
        ])}
      </Tabs>

      <Box ref={listTopRef} sx={{ scrollMarginTop: 80 }} />

      {filterTesterUserId && (tab === "items" || isCoverageTab) && (
        <VerifierSummaryCard
          summary={verifierSummary}
          loading={verifierSummaryLoading}
          error={verifierSummaryError}
          onOpenSection={(sectionTab) => { setPapersPage(1); setFilterPaperCoverage("verifier_any"); setTab(sectionTab); }}
        />
      )}

      {/* ── By Item ──────────────────────────────────────────────────────────── */}
      {tab === "items" && (
        <>
          <FilterBar onReset={resetFilters} onRefresh={refreshCurrentTab} refreshing={refreshing} footer={dateRange}>
            {typeFilter}
            {testerFilter}
            {platformFilter}
            {coverageFilter}
            {verdictFilter}
            {minVerifiersFilter}
            {questionIdFilter}
          </FilterBar>

          <SummaryTiles
            tiles={itemTiles}
            active={filterCoverage}
            onPick={(key) => { resetAllPages(); setFilterCoverage(key); }}
          />

          {itemsLoading && items.length === 0 ? (
            <LoadingBlock />
          ) : itemsError ? (
            <LoadError what="verified items" error={itemsError} onRetry={() => fetchItems(itemsPage)} />
          ) : items.length === 0 ? (
            <EmptyBlock>No verified items found for the selected filters.</EmptyBlock>
          ) : (
            <>
              <DashboardPagination compact page={itemsPage} totalPages={itemsTotalPages} total={itemsTotal} noun="items" disabled={itemsLoading} onChange={goToPage(setItemsPage)} />
              <TableContainer component={Paper} sx={{ ...TABLE_SX, opacity: itemsLoading ? 0.55 : 1, transition: "opacity .15s" }}>
                {itemsLoading && <LinearProgress />}
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Type</TableCell>
                      <TableCell>Item / question</TableCell>
                      <TableCell>Verdicts</TableCell>
                      <TableCell>Web</TableCell>
                      <TableCell>Mobile</TableCell>
                      <TableCell>Testers</TableCell>
                      <TableCell>Last verified</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {items.map((item) => (
                      <ItemAggregateRow
                        key={`${item.verifiable_type}:${item.verifiable_id}`}
                        item={item}
                        onVerify={handleOpenVerify}
                        onView={(id) => handleLoadQuestion(id, "view")}
                        onEdit={(id) => handleLoadQuestion(id, "edit")}
                      />
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <DashboardPagination
                page={itemsPage}
                totalPages={itemsTotalPages}
                total={itemsTotal}
                noun="items"
                disabled={itemsLoading}
                onChange={goToPage(setItemsPage)}
              />
            </>
          )}
        </>
      )}

      {/* ── By Paper / By Worksheet / By Mock Exam ───────────────────────────── */}
      {isCoverageTab && (
        <>
          <FilterBar
            onReset={resetFilters}
            onRefresh={refreshCurrentTab}
            refreshing={refreshing}
            footer={dateRange}
          >
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3, alignItems: "center", width: "100%" }}>
              {!isWorksheetTab && (
                <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: "text.secondary" }}>Check</Typography>
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    color="primary"
                    value={isMockExamTab ? "mock" : (filterPaperShelf || "all")}
                    onChange={(_, value) => {
                      if (!value) return;
                      setPapersPage(1);
                      if (value === "mock") {
                        setTab("mock-exams");
                        return;
                      }
                      setTab("papers");
                      setFilterPaperShelf(value === "all" ? "" : value);
                    }}
                  >
                    <ToggleButton value="practice" sx={{ textTransform: "none", px: 1.5, py: 0.5 }}>Practice papers</ToggleButton>
                    <ToggleButton value="mock" sx={{ textTransform: "none", px: 1.5, py: 0.5 }}>Mock exams</ToggleButton>
                    <ToggleButton value="past" sx={{ textTransform: "none", px: 1.5, py: 0.5 }}>Past papers</ToggleButton>
                    <ToggleButton value="all" sx={{ textTransform: "none", px: 1.5, py: 0.5 }}>All papers</ToggleButton>
                  </ToggleButtonGroup>
                </Stack>
              )}
              <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography variant="body2" sx={{ fontWeight: 600, color: "text.secondary" }}>Checked by</Typography>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  color="primary"
                  value={filterTesterUserId ? "person" : (filterChecker || "anyone")}
                  onChange={(_, value) => {
                    if (!value || value === "person") return;
                    setPapersPage(1);
                    setFilterTesterUserId("");
                    setFilterChecker(value === "anyone" ? "" : value);
                  }}
                >
                  <ToggleButton value="anyone" sx={{ textTransform: "none", px: 1.5, py: 0.5 }}>Anyone</ToggleButton>
                  <ToggleButton value="grok" sx={{ textTransform: "none", px: 1.5, py: 0.5 }}>
                    Grok QA bot{papersSummary.checker ? ` (${fmtNum(papersSummary.checker.grok)})` : ""}
                  </ToggleButton>
                  <ToggleButton value="manual" sx={{ textTransform: "none", px: 1.5, py: 0.5 }}>
                    Other testers{papersSummary.checker ? ` (${fmtNum(papersSummary.checker.manual)})` : ""}
                  </ToggleButton>
                  <ToggleButton value="needs_manual" sx={{ textTransform: "none", px: 1.5, py: 0.5 }}>
                    Grok only, needs manual{papersSummary.checker ? ` (${fmtNum(papersSummary.checker.needs_manual)})` : ""}
                  </ToggleButton>
                </ToggleButtonGroup>
              </Stack>
            </Box>
            {testerFilter}
            {platformFilter}
            {isMockExamTab ? (
              <Autocomplete
                freeSolo
                options={examOptions}
                getOptionLabel={(o) => (typeof o === "string" ? o : o.name ?? "")}
                isOptionEqualToValue={(o, v) => String(o.id) === String(v.id)}
                value={examOptions.find((o) => o.name === filterPaperSearch) ?? filterPaperSearch}
                inputValue={filterPaperSearch}
                onInputChange={(_, value, reason) => {
                  if (reason === "reset" && !value) return;
                  setPapersPage(1);
                  setFilterPaperSearch(value);
                }}
                onChange={(_, selected) => {
                  setPapersPage(1);
                  setFilterPaperSearch(typeof selected === "string" ? selected : selected?.name ?? "");
                }}
                size="small"
                sx={{ width: 240 }}
                renderInput={(params) => <TextField {...params} label="Mock exam" />}
              />
            ) : (
              <TextField
                size="small"
                label={isWorksheetTab ? "Search worksheet" : "Search paper"}
                placeholder={isWorksheetTab ? "Worksheet name" : "Paper name"}
                value={filterPaperSearch}
                onChange={(e) => { setPapersPage(1); setFilterPaperSearch(e.target.value); }}
                sx={{ width: 220 }}
              />
            )}
            {isMockExamTab && (
              <FormControl size="small" sx={{ minWidth: 170 }}>
                <InputLabel>Category</InputLabel>
                <Select
                  value={filterExamCategoryId}
                  label="Category"
                  onChange={(e) => { setPapersPage(1); setFilterExamCategoryId(e.target.value); }}
                >
                  <MenuItem value="">All categories</MenuItem>
                  {examCategoryOptions.map((c) => (
                    <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Subject</InputLabel>
              <Select
                value={filterPaperSubjectId}
                label="Subject"
                onChange={(e) => { setPapersPage(1); setFilterPaperSubjectId(e.target.value); }}
              >
                <MenuItem value="">All subjects</MenuItem>
                {subjectOptions.map((sub) => (
                  <MenuItem key={sub.id} value={sub.id}>{sub.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {isWorksheetTab && (
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Difficulty</InputLabel>
                <Select
                  value={filterDifficulty}
                  label="Difficulty"
                  onChange={(e) => { setPapersPage(1); setFilterDifficulty(e.target.value); }}
                >
                  <MenuItem value="">All levels</MenuItem>
                  <MenuItem value="Easy">Easy</MenuItem>
                  <MenuItem value="Medium">Medium</MenuItem>
                  <MenuItem value="Hard">Hard</MenuItem>
                </Select>
              </FormControl>
            )}
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Coverage</InputLabel>
              <Select
                value={filterPaperCoverage}
                label="Coverage"
                onChange={(e) => { setPapersPage(1); setFilterPaperCoverage(e.target.value); }}
              >
                <MenuItem value="all">{`All ${coverageEntity}s`}</MenuItem>
                <MenuItem value="started">Verification started</MenuItem>
                <MenuItem value="full">Fully verified only</MenuItem>
                <MenuItem value="partial">Partially verified</MenuItem>
                <MenuItem value="approved">{`${coverageEntityTitle} approved (sign-off)`}</MenuItem>
                <MenuItem value="not_approved">{`No ${coverageEntity} sign-off`}</MenuItem>
                <MenuItem value="not_started">Not started</MenuItem>
                {filterTesterUserId && <Divider />}
                {filterTesterUserId && <MenuItem value="verifier_all">{`Every question verified by ${selectedVerifierName}`}</MenuItem>}
                {filterTesterUserId && <MenuItem value="verifier_any">{`Some verified by ${selectedVerifierName}`}</MenuItem>}
                {filterTesterUserId && <MenuItem value="verifier_none">{`None verified by ${selectedVerifierName}`}</MenuItem>}
              </Select>
            </FormControl>
          </FilterBar>

          <SummaryTiles
            tiles={coverageTiles}
            active={filterPaperCoverage}
            onPick={(key) => { setPapersPage(1); setFilterPaperCoverage(key); }}
          />

          {!papersError && coverageRows.length > 0 && (
            <DashboardPagination compact page={papersPage} totalPages={papersTotalPages} total={papersTotal} noun={`${coverageEntity}s`} disabled={papersLoading} onChange={goToPage(setPapersPage)} />
          )}
          <PaperCoveragePanel
            papers={coverageRows}
            loading={papersLoading}
            error={papersError}
            onRetry={() => fetchPapers(papersPage)}
            entityLabel={coverageEntity}
            verifierName={selectedVerifierName}
            onViewPaper={isWorksheetTab ? undefined : (paperId, row) => {
              const qbFilter = isMockExamTab
                ? { filterMockExamName: row?.paper_name ?? "", filterPaperId: "" }
                : { filterPaperId: String(paperId), filterMockExamName: "" };
              // Carry the "Verified by" choice over so the Question Bank shows that verifier's questions.
              if (filterTesterUserId) {
                qbFilter.filterTesterUserId = String(filterTesterUserId);
                qbFilter.filterVerifierPlatform = filterPlatform || "";
              }
              try {
                const existing = JSON.parse(sessionStorage.getItem(QB_FILTER_KEY) || "{}");
                sessionStorage.setItem(QB_FILTER_KEY, JSON.stringify({ ...existing, ...qbFilter, page: 0 }));
              } catch {}
              navigate("/admin/question-bank");
            }}
            onOpenQuestion={openQuestionForVerification}
            openingQuestion={openingQuestionId}
          />
          {!papersError && (
            <DashboardPagination
              page={papersPage}
              totalPages={papersTotalPages}
              total={papersTab === tab ? papersTotal : 0}
              disabled={papersLoading}
              noun={`${coverageEntity}s`}
              onChange={goToPage(setPapersPage)}
            />
          )}
        </>
      )}

      {/* ── Individual Records ───────────────────────────────────────────────── */}
      {tab === "records" && (
        <>
          <FilterBar onReset={resetFilters} onRefresh={refreshCurrentTab} refreshing={refreshing} footer={dateRange}>
            {typeFilter}
            {testerFilter}
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Status</InputLabel>
              <Select value={filterStatus} label="Status" onChange={(e) => { resetAllPages(); setFilterStatus(e.target.value); }}>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
                <MenuItem value="flagged">Flagged</MenuItem>
              </Select>
            </FormControl>
            {platformFilter}
            {minVerifiersFilter}
          </FilterBar>

          {rowsLoading && rows.length === 0 ? (
            <LoadingBlock />
          ) : rowsError ? (
            <LoadError what="verifications" error={rowsError} onRetry={() => fetchRows(rowsPage)} />
          ) : rows.length === 0 ? (
            <EmptyBlock>No verifications found for the selected filters.</EmptyBlock>
          ) : (
            <>
              <DashboardPagination compact page={rowsPage} totalPages={rowsTotalPages} total={rowsTotal} noun="verdicts" disabled={rowsLoading} onChange={goToPage(setRowsPage)} />
              <TableContainer component={Paper} sx={{ ...TABLE_SX, opacity: rowsLoading ? 0.55 : 1, transition: "opacity .15s" }}>
                {rowsLoading && <LinearProgress />}
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Type</TableCell>
                      <TableCell>Item</TableCell>
                      <TableCell>Tester</TableCell>
                      <TableCell>Verdict</TableCell>
                      <TableCell>Platform</TableCell>
                      <TableCell>Comments</TableCell>
                      <TableCell>When</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((row) => (
                      <VerificationRow key={row.id} row={row} />
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <DashboardPagination
                page={rowsPage}
                totalPages={rowsTotalPages}
                total={rowsTotal}
                noun="verdicts"
                disabled={rowsLoading}
                onChange={goToPage(setRowsPage)}
              />
            </>
          )}
        </>
      )}

      {/* ── Explanation videos ───────────────────────────────────────────────── */}
      {tab === "videos" && <VideoVerificationPanel />}

      {/* ── Tester Activity ──────────────────────────────────────────────────── */}
      {tab === "activity" && (
        <>
          <FilterBar
            onReset={resetFilters}
            onRefresh={refreshCurrentTab}
            refreshing={refreshing}
            footer={<DateRange from={filterDateFrom} to={filterDateTo} onChange={setDates} allowAll={false} />}
          >
            {typeFilter}
            {testerFilter}
            {platformFilter}
          </FilterBar>
          {!activityError && activityRows.length > 0 && (
            <DashboardPagination compact page={activityPage} totalPages={activityTotalPages} total={activityTotal} noun="verdicts" disabled={activityLoading} onChange={goToPage(setActivityPage)} />
          )}
          <TesterActivityPanel rows={activityRows} loading={activityLoading} error={activityError} onRetry={() => fetchActivity(activityPage)} />
          {!activityError && (
            <DashboardPagination
              page={activityPage}
              totalPages={activityTotalPages}
              total={activityTotal}
              noun="verdicts"
                disabled={activityLoading}
              onChange={goToPage(setActivityPage)}
            />
          )}
        </>
      )}

      {/* ── Tester Stats ─────────────────────────────────────────────────────── */}
      {tab === "tester-stats" && (
        <>
          <FilterBar onReset={resetFilters} onRefresh={refreshCurrentTab} refreshing={refreshing} footer={dateRange}>
            {typeFilter}
            {platformFilter}
          </FilterBar>
          <TesterStatsPanel
            stats={testerStats}
            loading={testerStatsLoading}
            error={testerStatsError}
            onRetry={() => fetchTesterStats(statsPage)}
            onShowItems={(userId) => {
              resetAllPages();
              setFilterTesterUserId(String(userId));
              setTab("items");
            }}
          />
          {!testerStatsError && (
            <DashboardPagination
              page={statsPage}
              totalPages={statsTotalPages}
              total={statsTotal}
              noun="testers"
                disabled={testerStatsLoading}
              onChange={goToPage(setStatsPage)}
            />
          )}
        </>
      )}

      {/* ── Tester Edits ─────────────────────────────────────────────────────── */}
      {tab === "edits" && (
        <>
          <FilterBar onReset={resetFilters} onRefresh={refreshCurrentTab} refreshing={refreshing} footer={dateRange}>
            {testerFilter}
            {platformFilter}
            {questionIdFilter}
            <FormControlLabel
              control={<Checkbox size="small" checked={hideFormattingEdits} onChange={(e) => setHideFormattingEdits(e.target.checked)} />}
              label={<Typography variant="body2">Hide formatting-only edits</Typography>}
              sx={{ ml: 0 }}
            />
          </FilterBar>
          {!editsError && edits.length > 0 && (
            <DashboardPagination compact page={editsPage} totalPages={editsTotalPages} total={editsTotal} noun="edits" disabled={editsLoading} onChange={goToPage(setEditsPage)} />
          )}
          <TesterEditsPanel
            edits={edits}
            loading={editsLoading}
            error={editsError}
            onRetry={() => fetchEdits(editsPage)}
            hideFormatting={hideFormattingEdits}
          />
          {!editsError && (
            <DashboardPagination
              page={editsPage}
              totalPages={editsTotalPages}
              total={editsTotal}
              noun="edits"
                disabled={editsLoading}
              onChange={goToPage(setEditsPage)}
            />
          )}
        </>
      )}

      {/* ── Question view dialog ─────────────────────────────────────────────── */}
      <Dialog open={viewQDialogOpen} onClose={() => { setViewQDialogOpen(false); setViewQVerifications([]); }} maxWidth="md" fullWidth scroll="paper">
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>Question Details</Typography>
            {viewQData && <Typography variant="body2" color="text.secondary">ID: #{viewQData.id}</Typography>}
          </Box>
          <IconButton onClick={() => { setViewQDialogOpen(false); setViewQVerifications([]); }} size="small"><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {viewQLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}><CircularProgress /></Box>
          ) : viewQData ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
                <Chip label={`#${viewQData.id}`} size="small" sx={{ fontWeight: 700, bgcolor: "#f3e5f5", color: "#7b1fa2" }} />
                <Chip label={viewQData.type} size="small" variant="outlined" />
                {viewQData.difficulty && <Chip label={viewQData.difficulty} size="small" color="info" variant="outlined" />}
                {viewQData.default_marks != null && (
                  <Chip label={`${viewQData.default_marks} mark${viewQData.default_marks !== 1 ? "s" : ""}`} size="small" variant="outlined" />
                )}
                {viewQData.category_name && <Chip label={viewQData.category_name} size="small" color="default" variant="outlined" />}
              </Box>
              <Divider />
              {viewQData.question_text && (
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: "block", mb: 0.5 }}>Question</Typography>
                  <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1, bgcolor: "#fafafa" }}>
                    <HtmlWithLatex html={viewQData.question_text} />
                  </Paper>
                </Box>
              )}
              {viewQData.image_url && (
                <Box component="img" src={viewQData.image_url} alt="" sx={{ maxWidth: "100%", maxHeight: 240, borderRadius: 1, border: "1px solid", borderColor: "divider", objectFit: "contain" }} />
              )}
              {["multiple-choice", "multiple-select", "true-false"].includes(viewQData.type) && viewQData.options?.length > 0 && (
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: "block", mb: 0.5 }}>Options</Typography>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                    {viewQData.options.map((opt, i) => (
                      <Box key={opt.id ?? i} sx={{ display: "flex", alignItems: "center", gap: 1, p: 1, borderRadius: 1, border: "1px solid", borderColor: opt.is_correct ? "success.main" : "#e0e0e0", bgcolor: opt.is_correct ? "rgba(46,125,50,0.05)" : "transparent" }}>
                        {opt.is_correct
                          ? <CheckCircleOutlineIcon sx={{ color: "success.main", fontSize: 16, flexShrink: 0 }} />
                          : <CancelOutlinedIcon sx={{ color: "#bdbdbd", fontSize: 16, flexShrink: 0 }} />}
                        <Typography variant="body2" sx={{ fontWeight: opt.is_correct ? 600 : 400 }}>{opt.option_text || opt.text || ""}</Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
              {viewQData.answer?.correct_answer_text && (
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: "block", mb: 0.5 }}>Answer</Typography>
                  <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1, bgcolor: "rgba(46,125,50,0.05)", borderColor: "success.light" }}>
                    <Box sx={{ "& p": { m: 0 } }}>
                      <HtmlWithLatex html={String(viewQData.answer.correct_answer_text)} />
                    </Box>
                  </Paper>
                </Box>
              )}
              {viewQData.answer?.correct_blanks?.length > 0 && (
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: "block", mb: 0.5 }}>Blank Answers</Typography>
                  <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                    {viewQData.answer.correct_blanks.map((b, i) => (
                      <Chip key={i} label={Array.isArray(b) ? b[0] : b} size="small" color="success" variant="outlined" />
                    ))}
                  </Box>
                </Box>
              )}
              {viewQData.type === "grouped" && viewQData.sub_questions?.length > 0 && (
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: "block", mb: 1 }}>
                    Sub-questions ({viewQData.sub_questions.length})
                  </Typography>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    {viewQData.sub_questions.map((sq, idx) => (
                      <Paper key={sq.id ?? idx} variant="outlined" sx={{ p: 1.5, borderRadius: 1 }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: "#7b1fa2", display: "block", mb: 0.5 }}>
                          Part {idx + 1} — {sq.type}
                        </Typography>
                        {sq.question_text && <HtmlWithLatex html={sq.question_text} />}
                        {sq.answer?.correct_answer_text && (
                          <Box sx={{ mt: 0.5, "& p": { m: 0 } }}>
                            <Typography component="span" variant="body2" sx={{ color: "success.dark", fontWeight: 600 }}>Answer: </Typography>
                            <HtmlWithLatex html={sq.answer.correct_answer_text} />
                          </Box>
                        )}
                        {sq.answer?.correct_blanks?.length > 0 && (
                          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mt: 0.5 }}>
                            {sq.answer.correct_blanks.map((b, bi) => (
                              <Chip key={bi} label={Array.isArray(b) ? b[0] : b} size="small" color="success" variant="outlined" />
                            ))}
                          </Box>
                        )}
                      </Paper>
                    ))}
                  </Box>
                </Box>
              )}
              {viewQData.explanation && (
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: "block", mb: 0.5 }}>Explanation</Typography>
                  <HtmlWithLatex html={viewQData.explanation} />
                </Box>
              )}
              {Array.isArray(viewQData.topics) && viewQData.topics.length > 0 && (
                <Box>
                  <Divider sx={{ mb: 1.5 }} />
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: "block", mb: 0.75 }}>Topics</Typography>
                  <Stack direction="row" spacing={0.75} flexWrap="wrap">
                    {viewQData.topics.map((t) => (
                      <Chip key={t.id ?? t.name} label={t.name} size="small" variant="outlined" sx={{ fontSize: "0.72rem" }} />
                    ))}
                  </Stack>
                </Box>
              )}
              {Array.isArray(viewQData.linked_papers) && viewQData.linked_papers.length > 0 && (
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: "block", mb: 0.75 }}>
                    Linked Papers ({viewQData.linked_papers.length})
                  </Typography>
                  <Stack direction="row" spacing={0.75} flexWrap="wrap">
                    {viewQData.linked_papers.map((p) => (
                      <Chip key={p.id ?? p.title} label={p.title ?? p.name ?? `#${p.id}`} size="small" color="warning" variant="outlined" sx={{ fontSize: "0.72rem" }} />
                    ))}
                  </Stack>
                </Box>
              )}
              <Box>
                <Divider sx={{ mb: 1.5 }} />
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: "block", mb: 0.75 }}>Verified By</Typography>
                {viewQVerificationsLoading ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CircularProgress size={14} />
                    <Typography variant="caption" color="text.secondary">Loading…</Typography>
                  </Box>
                ) : viewQVerifications.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>Not yet verified by any tester.</Typography>
                ) : (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                    {viewQVerifications.map((v) => {
                      const cfg = v.status === "approved"
                        ? { label: "Approved", color: "success" }
                        : v.status === "rejected"
                          ? { label: "Rejected", color: "error" }
                          : { label: v.status, color: "default" };
                      return (
                        <Box key={v.id} sx={{ display: "flex", alignItems: "center", gap: 1, p: 1, borderRadius: 1.5, border: "1px solid #e0e0e0", bgcolor: "#fafafa" }}>
                          <Typography variant="body2" sx={{ flex: 1, fontWeight: 600 }}>{v.user?.name ?? `User #${v.user_id}`}</Typography>
                          <Chip label={cfg.label} color={cfg.color} size="small" variant="outlined" />
                          <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
                            {v.verified_at ? new Date(v.verified_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : "—"}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </Box>
            </Box>
          ) : (
            <Typography color="text.secondary">No data available.</Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ gap: 1 }}>
          {viewQData && (
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={() => {
                setViewQDialogOpen(false);
                setViewQVerifications([]);
                setEditQData(viewQData);
                setEditQOpen(true);
              }}
              sx={{ textTransform: "none" }}
            >
              Edit Question
            </Button>
          )}
          <Button onClick={() => { setViewQDialogOpen(false); setViewQVerifications([]); }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ── Accept / reject dialog ───────────────────────────────────────────── */}
      <QuestionVerifyDialog
        open={verifyOpen}
        question={verifyQuestion}
        itemData={verifyItemData}
        loading={verifyLoading}
        submitting={verifySubmitting}
        onClose={() => { setVerifyOpen(false); setVerifyItem(null); setVerifyQuestion(null); }}
        onSubmit={handleSubmitVerify}
        onEdit={(q) => {
          setVerifyOpen(false);
          setEditQData(q);
          setEditQOpen(true);
        }}
      />

      {/* ── Question edit dialog ─────────────────────────────────────────────── */}
      {editQOpen && editQData && (
        <QuestionBankQuestionEditDialog
          question={editQData}
          saving={editQSaving}
          onClose={() => { setEditQOpen(false); setEditQData(null); }}
          onSave={async (payload) => {
            setEditQSaving(true);
            try {
              await api.put(`/admin/question-bank/${editQData.id}`, payload);
              toast.success("Question saved.");
              setEditQOpen(false);
              setEditQData(null);
              refreshCurrentTab();
            } catch (err) {
              const errs = err?.response?.data?.errors;
              const first = errs ? Object.values(errs)[0]?.[0] : null;
              toast.error(first || err?.response?.data?.message || "Failed to save question.");
            } finally {
              setEditQSaving(false);
            }
          }}
        />
      )}
    </Box>
  );
}
